import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { createPortal } from 'react-dom';
import { collection, addDoc, getDocs, getDoc, deleteDoc, doc, updateDoc, query, where, deleteField } from 'firebase/firestore';
import { db } from '../../firebase';
import { useAuth, ROLES } from '../../hooks/useAuth';
import { PRODUCT_TYPES, ORDER_STATUS, ORDER_STATUS_LABELS, TUL_PILE_MODELS, KANAT_PILE_MODELS, PAYMENT_TYPES, PAYMENT_TYPE_LABELS } from '../../constants';
import { formatPrice, calculatePileDensity, calculateUsedM2, calculateCapsulePrice, calculateTotalPrice, calculateDiscountedPrice, calculateStorZebraUsedM2, calculateStorZebraTotalPrice, calculateJaluziUsedM2, calculateJaluziTotalPrice, calculateDikeyPerdeTotalPrice, calculatePliseUsedM2, calculatePliseTotalPrice, calculateAksesuarTotalPrice, calculateKatlamaliPerdeUsedM, calculateKatlamaliPerdeTotalPrice, calculateTotalCost, calculateRustikUsedCm, calculateRustikAyakSayisi, calculateRustikTotals, cloneRustikExtras, sanitizeRustikExtrasForSave } from '../../utils';
import { generateCustomerPDF, generateCustomerPreOrderPDF, generateWorkshopPDF, generateInstallationPDF } from '../../utils/pdfGenerators';
import { calculateCustomerGrandTotal } from '../../utils/customerFinancialUtils';
import { formatDealerBalanceLabel } from '../../utils/dealerSessionBalanceUtils';
import OrderForm from './OrderForm';
import './OrderDetails.css';
// Helper functions for calculations
const getMotorCostTL = (price, currency, rates) => {
    const p = parseFloat(price) || 0;
    if (!currency || currency === 'TL' || currency === 'TRY') return p;
    const rate = parseFloat(rates[currency]) || 1;
    return p * rate;
};

const OrderDetails = ({
    customerId,
    customerName,
    customerInfo,
    isLoading,
    orderStatus,
    onOrderStatusChange,
    onCustomerSaved,
    isEditMode, // Düzenleme modu mu?
    onVadeliYuzdeChange, // Vadeli yüzde değiştiğinde parent'a bildir
    onGenelIskontoYuzdeChange, // Genel iskonto yüzde değiştiğinde parent'a bildir
    onSaveVadeliSuccess, // Vadeli/İskonto kaydetme başarılı olduğunda bildir
    permissions = {}, // Rol bazlı yetkiler
    parentCollectionName = "customers", // Koleksiyon adı, varsayılan customers
    onOrderSaved = null, // Sipariş kaydedildiğinde bildir (bayi modu)
    isWholesale = false, // Bayi modu: toptan fiyat kullan
    onSessionDateChange = null, // Sipariş/teslimat tarihi değişince bildir
    onFormStateChange = null // Form açık/kapalı durumunu parent'a bildir
}) => {
    const { user, isMontajci } = useAuth();
    const [orders, setOrders] = useState([]);
    const [isAddingOrder, setIsAddingOrder] = useState(false);
    const [selectedProductType, setSelectedProductType] = useState('');
    const [editingOrderId, setEditingOrderId] = useState(null); // Edit modu için
    const [deleteConfirm, setDeleteConfirm] = useState(null); // Silme onayı için
    const [vadeliYuzde, setVadeliYuzde] = useState(''); // Vadeli yüzde için
    const [genelIskontoYuzde, setGenelIskontoYuzde] = useState(''); // Genel iskonto yüzde için
    const [isSavingVadeli, setIsSavingVadeli] = useState(false); // Vadeli/İskonto kaydetme durumu
    const [paidAmount, setPaidAmount] = useState(''); // Ödenen tutar
    const [isPaymentCompleted, setIsPaymentCompleted] = useState(false); // Ödeme tamamlandı mı
    const [isSavingPayment, setIsSavingPayment] = useState(false); // Ödeme kaydetme durumu
    const [pileModels, setPileModels] = useState([]); // Pile modelleri
    const [showSystemConfirm, setShowSystemConfirm] = useState(false); // Sistem seçim onayı için
    // Ödeme geçmişi state'leri
    const [paymentHistory, setPaymentHistory] = useState([]); // Ödeme geçmişi listesi
    const [newPaymentAmount, setNewPaymentAmount] = useState(''); // Yeni ödeme tutarı
    const [newPaymentType, setNewPaymentType] = useState(PAYMENT_TYPES.CASH); // Yeni ödeme tipi
    const [isAddingPayment, setIsAddingPayment] = useState(false); // Ödeme ekleme durumu
    const [cancelPaymentConfirm, setCancelPaymentConfirm] = useState(null); // İptal edilecek ödeme
    // Manuel toplam düzenleme
    const [manualTotalOverride, setManualTotalOverride] = useState(null); // null = otomatik hesaplama
    const [isEditingTotal, setIsEditingTotal] = useState(false); // Toplam düzenleme modu
    const [rustikFormSessionKey, setRustikFormSessionKey] = useState(0);
    const [editingTotalValue, setEditingTotalValue] = useState(''); // Düzenleme sırasındaki geçici değer
    const [isSavingTotal, setIsSavingTotal] = useState(false); // Toplam kaydetme durumu
    const [toast, setToast] = useState({ show: false, message: '', type: 'error' }); // Bildirim sistemi
    // Bayi modu: Sipariş ve teslimat tarihleri
    const [sessionOrderDate, setSessionOrderDate] = useState('');
    const [sessionDeliveryDate, setSessionDeliveryDate] = useState('');
    const [isSavingSessionDates, setIsSavingSessionDates] = useState(false);
    const [adminSettings, setAdminSettings] = useState({
        sPilePrice: 0, sPileWholesale: 0, sPileCost: 0, american2Price: 0, american2Wholesale: 0, american2Cost: 0, american3Price: 0, american3Wholesale: 0, american3Cost: 0,
        kanatSPilePrice: 0, kanatSPileWholesale: 0, kanatSPileCost: 0, kanatAmerican2Price: 0, kanatAmerican2Wholesale: 0, kanatAmerican2Cost: 0, kanatAmerican3Price: 0, kanatAmerican3Wholesale: 0, kanatAmerican3Cost: 0,
        kanatKapsulPrice: 0, kanatKapsulWholesale: 0, kanatKapsulCost: 0,
        motorCurrency: 'USD', zigilPrice: 0, zigilWholesale: 0, zigilCost: 0, reduktorPrice: 0, reduktorWholesale: 0, reduktorCost: 0, kurdelaPrice: 0, kurdelaWholesale: 0, kurdelaCost: 0,
        metalZigilPrice: 0, metalZigilWholesale: 0, metalZigilCost: 0, metalReduktorPrice: 0, metalReduktorWholesale: 0, metalReduktorCost: 0, ikiCamArasiPrice: 0, ikiCamArasiWholesale: 0, ikiCamArasiCost: 0,
        storJumboPrice: 0, storJumboWholesale: 0, storJumboCost: 0, storAltEtekDilimPrice: 0, storAltEtekDilimWholesale: 0, storAltEtekDilimCost: 0, storKapaliKasaPrice: 0, storKapaliKasaWholesale: 0, storKapaliKasaCost: 0,
        storAltEtekLazerPrice: 0, storAltEtekLazerWholesale: 0, storAltEtekLazerCost: 0, storDekoratifLamaPrice: 0, storDekoratifLamaWholesale: 0, storDekoratifLamaCost: 0, storDekoratifKasaPrice: 0, storDekoratifKasaWholesale: 0, storDekoratifKasaCost: 0,
        storAltEtekBeyazIsPrice: 0, storAltEtekBeyazIsWholesale: 0, storAltEtekBeyazIsCost: 0,
        zebraJumboPrice: 0, zebraJumboWholesale: 0, zebraJumboCost: 0, zebraAltEtekDilimPrice: 0, zebraAltEtekDilimWholesale: 0, zebraAltEtekDilimCost: 0, zebraKapaliKasaPrice: 0, zebraKapaliKasaWholesale: 0, zebraKapaliKasaCost: 0,
        dikeyFonPrice: 0, dikeyFonWholesale: 0, dikeyFonCost: 0, dikeyFonList: [],
        katlamaliMekanizmaList: [],
        rustikAyakModelList: [],
        rustikAyakCapList: [],
        rustikAyakUzunlukList: [],
        rustikAyakRenkKoduList: [],
        katlamaliPerdeMotorCost: 0, katlamaliPerdeMotorRetail: 0, katlamaliPerdeMotorWholesale: 0, katlamaliPerdeMotorCurrency: 'USD', katlamaliPerdeMotorSpecialRateRetail: 0, katlamaliPerdeMotorSpecialRateWholesale: 0,
        zebraMotorCost: 0, zebraMotorRetail: 0, zebraMotorWholesale: 0, zebraMotorCurrency: 'USD', zebraMotorSpecialRateRetail: 0, zebraMotorSpecialRateWholesale: 0,
        ahsapJaluziMotorCost: 0, ahsapJaluziMotorRetail: 0, ahsapJaluziMotorWholesale: 0, ahsapJaluziMotorCurrency: 'USD', ahsapJaluziMotorSpecialRateRetail: 0, ahsapJaluziMotorSpecialRateWholesale: 0,
        metalJaluziMotorCost: 0, metalJaluziMotorRetail: 0, metalJaluziMotorWholesale: 0, metalJaluziMotorCurrency: 'USD', metalJaluziMotorSpecialRateRetail: 0, metalJaluziMotorSpecialRateWholesale: 0,
        dikeyPerdeMotorCost: 0, dikeyPerdeMotorRetail: 0, dikeyPerdeMotorWholesale: 0, dikeyPerdeMotorCurrency: 'USD', dikeyPerdeMotorSpecialRateRetail: 0, dikeyPerdeMotorSpecialRateWholesale: 0,
        dikeyPvcMotorCost: 0, dikeyPvcMotorRetail: 0, dikeyPvcMotorWholesale: 0, dikeyPvcMotorCurrency: 'USD', dikeyPvcMotorSpecialRateRetail: 0, dikeyPvcMotorSpecialRateWholesale: 0,
        dikeyStorMotorCost: 0, dikeyStorMotorRetail: 0, dikeyStorMotorWholesale: 0, dikeyStorMotorCurrency: 'USD', dikeyStorMotorSpecialRateRetail: 0, dikeyStorMotorSpecialRateWholesale: 0,
        kapsulPrice: 0, kapsulWholesale: 0, kapsulCost: 0
    });
    const [exchangeRates, setExchangeRates] = useState({ USD: 1, EUR: 1 });
    const showToast = useCallback((message, type = 'error') => {
        setToast({ show: true, message, type });
        setTimeout(() => {
            setToast(prev => ({ ...prev, show: false }));
        }, 4000);
    }, []);
    // Form açık/kapalı durumunu parent'a bildir (DealerList'teki İşlemi Tamamla butonunu pasifleştirmek için)
    useEffect(() => {
        if (onFormStateChange) {
            onFormStateChange(isAddingOrder);
        }
    }, [isAddingOrder, onFormStateChange]);

    // Bayi modu: Oturumun sipariş ve teslimat tarihlerini başlat
    useEffect(() => {
        if (isWholesale && customerInfo) {
            const todayObj = new Date();
            const year = todayObj.getFullYear();
            const month = String(todayObj.getMonth() + 1).padStart(2, '0');
            const day = String(todayObj.getDate()).padStart(2, '0');
            const today = `${year}-${month}-${day}`;
            setSessionOrderDate(customerInfo.orderDate || customerInfo.sessionDate || today);
            setSessionDeliveryDate(customerInfo.deliveryDate || '');
        }
    }, [isWholesale, customerInfo]);

    // Bayi modu: Tarih değiştirince Firestore'a kaydet
    const handleSessionDateChange = useCallback(async (field, value) => {
        if (!customerId) return;
        if (field === 'orderDate') setSessionOrderDate(value);
        if (field === 'deliveryDate') setSessionDeliveryDate(value);
        setIsSavingSessionDates(true);
        try {
            await updateDoc(doc(db, 'dealer_sessions', customerId), {
                [field]: value,
                updatedAt: new Date()
            });
            // Parent'a bildir (tablo güncelleme için)
            if (onSessionDateChange) {
                onSessionDateChange(customerId, field, value);
            }
        } catch (err) {
            console.error('Tarih kaydedilemedi:', err);
            showToast('Tarih kaydedilemedi: ' + err.message);
        } finally {
            setIsSavingSessionDates(false);
        }
    }, [customerId, showToast, onSessionDateChange]);

    // Ayarları Firestore'dan çek
    useEffect(() => {
        const fetchSettings = async () => {
            try {
                const gSnap = await getDoc(doc(db, 'settings', 'global'));

                if (gSnap.exists()) {
                    const data = gSnap.data();
                    setAdminSettings(prev => ({
                        ...prev,
                        sPileCost: Number(data.sPileCost) || 0,
                        american2Cost: Number(data.american2Cost) || 0,
                        american3Cost: Number(data.american3Cost) || 0,
                        zigilCost: Number(data.zigilCost) || 0,
                        zigilPrice: Number(data.zigilPrice) || 0,
                        zigilWholesale: Number(data.zigilWholesale) || 0,
                        reduktorCost: Number(data.reduktorCost) || 0,
                        kurdelaCost: Number(data.kurdelaCost) || 0,
                        metalZigilCost: Number(data.metalZigilCost) || 0,
                        metalZigilPrice: Number(data.metalZigilPrice) || 0,
                        metalZigilWholesale: Number(data.metalZigilWholesale) || 0,
                        metalReduktorCost: Number(data.metalReduktorCost) || 0,
                        metalReduktorPrice: Number(data.metalReduktorPrice) || 0,
                        metalReduktorWholesale: Number(data.metalReduktorWholesale) || 0,
                        ikiCamArasiCost: Number(data.ikiCamArasiCost) || 0,
                        ikiCamArasiPrice: Number(data.ikiCamArasiPrice) || 0,
                        ikiCamArasiWholesale: Number(data.ikiCamArasiWholesale) || 0,
                        storJumboPrice: Number(data.storJumboPrice) || 0,
                        storJumboWholesale: Number(data.storJumboWholesale) || 0,
                        storJumboCost: Number(data.storJumboCost) || 0,
                        storAltEtekDilimPrice: Number(data.storAltEtekDilimPrice) || 0,
                        storAltEtekDilimWholesale: Number(data.storAltEtekDilimWholesale) || 0,
                        storAltEtekDilimCost: Number(data.storAltEtekDilimCost) || 0,
                        storKapaliKasaPrice: Number(data.storKapaliKasaPrice) || 0,
                        storKapaliKasaWholesale: Number(data.storKapaliKasaWholesale) || 0,
                        storKapaliKasaCost: Number(data.storKapaliKasaCost) || 0,
                        storAltEtekLazerPrice: Number(data.storAltEtekLazerPrice) || 0,
                        storAltEtekLazerWholesale: Number(data.storAltEtekLazerWholesale) || 0,
                        storAltEtekLazerCost: Number(data.storAltEtekLazerCost) || 0,
                        storDekoratifLamaPrice: Number(data.storDekoratifLamaPrice) || 0,
                        storDekoratifLamaWholesale: Number(data.storDekoratifLamaWholesale) || 0,
                        storDekoratifLamaCost: Number(data.storDekoratifLamaCost) || 0,
                        storDekoratifKasaPrice: Number(data.storDekoratifKasaPrice) || 0,
                        storDekoratifKasaWholesale: Number(data.storDekoratifKasaWholesale) || 0,
                        storDekoratifKasaCost: Number(data.storDekoratifKasaCost) || 0,
                        storAltEtekBeyazIsPrice: Number(data.storAltEtekBeyazIsPrice) || 0,
                        storAltEtekBeyazIsWholesale: Number(data.storAltEtekBeyazIsWholesale) || 0,
                        storAltEtekBeyazIsCost: Number(data.storAltEtekBeyazIsCost) || 0,
                        zebraJumboPrice: Number(data.zebraJumboPrice) || 0,
                        zebraJumboWholesale: Number(data.zebraJumboWholesale) || 0,
                        zebraJumboCost: Number(data.zebraJumboCost) || 0,
                        zebraAltEtekDilimPrice: Number(data.zebraAltEtekDilimPrice) || 0,
                        zebraAltEtekDilimWholesale: Number(data.zebraAltEtekDilimWholesale) || 0,
                        zebraAltEtekDilimCost: Number(data.zebraAltEtekDilimCost) || 0,
                        zebraKapaliKasaPrice: Number(data.zebraKapaliKasaPrice) || 0,
                        zebraKapaliKasaWholesale: Number(data.zebraKapaliKasaWholesale) || 0,
                        zebraKapaliKasaCost: Number(data.zebraKapaliKasaCost) || 0,
                        dikeyFonPrice: Number(data.dikeyFonPrice) || 0,
                        dikeyFonWholesale: Number(data.dikeyFonWholesale) || 0,
                        dikeyFonCost: Number(data.dikeyFonCost) || 0,
                        katlamaliMekanizmaList: data.katlamaliMekanizmaList || [],
                        // Ürün tipine özel motor maliyetleri
                        // Ürün tipine özel motor maliyetleri ve ayarları
                        storMotorCost: Number(data.storMotorCost) || 0,
                        storMotorRetail: Number(data.storMotorRetail) || 0,
                        storMotorWholesale: Number(data.storMotorWholesale) || 0,
                        storMotorCurrency: data.storMotorCurrency || 'USD',
                        storMotorSpecialRateRetail: Number(data.storMotorSpecialRateRetail) || 0,
                        storMotorSpecialRateWholesale: Number(data.storMotorSpecialRateWholesale) || 0,

                        zebraMotorCost: Number(data.zebraMotorCost) || 0,
                        zebraMotorRetail: Number(data.zebraMotorRetail) || 0,
                        zebraMotorWholesale: Number(data.zebraMotorWholesale) || 0,
                        zebraMotorCurrency: data.zebraMotorCurrency || 'USD',
                        zebraMotorSpecialRateRetail: Number(data.zebraMotorSpecialRateRetail) || 0,
                        zebraMotorSpecialRateWholesale: Number(data.zebraMotorSpecialRateWholesale) || 0,

                        ahsapJaluziMotorCost: Number(data.ahsapJaluziMotorCost) || 0,
                        ahsapJaluziMotorRetail: Number(data.ahsapJaluziMotorRetail) || 0,
                        ahsapJaluziMotorWholesale: Number(data.ahsapJaluziMotorWholesale) || 0,
                        ahsapJaluziMotorCurrency: data.ahsapJaluziMotorCurrency || 'USD',
                        ahsapJaluziMotorSpecialRateRetail: Number(data.ahsapJaluziMotorSpecialRateRetail) || 0,
                        ahsapJaluziMotorSpecialRateWholesale: Number(data.ahsapJaluziMotorSpecialRateWholesale) || 0,

                        metalJaluziMotorCost: Number(data.metalJaluziMotorCost) || 0,
                        metalJaluziMotorRetail: Number(data.metalJaluziMotorRetail) || 0,
                        metalJaluziMotorWholesale: Number(data.metalJaluziMotorWholesale) || 0,
                        metalJaluziMotorCurrency: data.metalJaluziMotorCurrency || 'USD',
                        metalJaluziMotorSpecialRateRetail: Number(data.metalJaluziMotorSpecialRateRetail) || 0,
                        metalJaluziMotorSpecialRateWholesale: Number(data.metalJaluziMotorSpecialRateWholesale) || 0,

                        dikeyPerdeMotorCost: Number(data.dikeyPerdeMotorCost) || 0,
                        dikeyPerdeMotorRetail: Number(data.dikeyPerdeMotorRetail) || 0,
                        dikeyPerdeMotorWholesale: Number(data.dikeyPerdeMotorWholesale) || 0,
                        dikeyPerdeMotorCurrency: data.dikeyPerdeMotorCurrency || 'USD',
                        dikeyPerdeMotorSpecialRateRetail: Number(data.dikeyPerdeMotorSpecialRateRetail) || 0,
                        dikeyPerdeMotorSpecialRateWholesale: Number(data.dikeyPerdeMotorSpecialRateWholesale) || 0,

                        dikeyPvcMotorCost: Number(data.dikeyPvcMotorCost) || 0,
                        dikeyPvcMotorRetail: Number(data.dikeyPvcMotorRetail) || 0,
                        dikeyPvcMotorWholesale: Number(data.dikeyPvcMotorWholesale) || 0,
                        dikeyPvcMotorCurrency: data.dikeyPvcMotorCurrency || 'USD',
                        dikeyPvcMotorSpecialRateRetail: Number(data.dikeyPvcMotorSpecialRateRetail) || 0,
                        dikeyPvcMotorSpecialRateWholesale: Number(data.dikeyPvcMotorSpecialRateWholesale) || 0,

                        dikeyStorMotorRetail: Number(data.dikeyStorMotorRetail) || 0,
                        dikeyStorMotorWholesale: Number(data.dikeyStorMotorWholesale) || 0,
                        dikeyStorMotorCurrency: data.dikeyStorMotorCurrency || 'USD',
                        dikeyStorMotorSpecialRateRetail: Number(data.dikeyStorMotorSpecialRateRetail) || 0,
                        dikeyStorMotorSpecialRateWholesale: Number(data.dikeyStorMotorSpecialRateWholesale) || 0,

                        katlamaliPerdeMotorCost: Number(data.katlamaliPerdeMotorCost) || 0,
                        katlamaliPerdeMotorRetail: Number(data.katlamaliPerdeMotorRetail) || 0,
                        katlamaliPerdeMotorWholesale: Number(data.katlamaliPerdeMotorWholesale) || 0,
                        katlamaliPerdeMotorCurrency: data.katlamaliPerdeMotorCurrency || 'USD',
                        katlamaliPerdeMotorSpecialRateRetail: Number(data.katlamaliPerdeMotorSpecialRateRetail) || 0,
                        katlamaliPerdeMotorSpecialRateWholesale: Number(data.katlamaliPerdeMotorSpecialRateWholesale) || 0,
                        kapsulCost: Number(data.kapsulCost) || 0,
                        kapsulPrice: Number(data.kapsulPrice) || 0,
                        kapsulWholesale: Number(data.kapsulWholesale) || 0,
                        kanatSPileCost: data.kanatSPileCost !== undefined ? Number(data.kanatSPileCost) : (Number(data.sPileCost) || 0),
                        kanatSPilePrice: data.kanatSPilePrice !== undefined ? Number(data.kanatSPilePrice) : (Number(data.sPilePrice) || 0),
                        kanatSPileWholesale: data.kanatSPileWholesale !== undefined ? Number(data.kanatSPileWholesale) : (Number(data.sPileWholesale) || 0),
                        kanatAmerican2Cost: data.kanatAmerican2Cost !== undefined ? Number(data.kanatAmerican2Cost) : (Number(data.american2Cost) || 0),
                        kanatAmerican2Price: data.kanatAmerican2Price !== undefined ? Number(data.kanatAmerican2Price) : (Number(data.american2Price) || 0),
                        kanatAmerican2Wholesale: data.kanatAmerican2Wholesale !== undefined ? Number(data.kanatAmerican2Wholesale) : (Number(data.american2Wholesale) || 0),
                        kanatAmerican3Cost: data.kanatAmerican3Cost !== undefined ? Number(data.kanatAmerican3Cost) : (Number(data.american3Cost) || 0),
                        kanatAmerican3Price: data.kanatAmerican3Price !== undefined ? Number(data.kanatAmerican3Price) : (Number(data.american3Price) || 0),
                        kanatAmerican3Wholesale: data.kanatAmerican3Wholesale !== undefined ? Number(data.kanatAmerican3Wholesale) : (Number(data.american3Wholesale) || 0),
                        kanatKapsulCost: data.kanatKapsulCost !== undefined ? Number(data.kanatKapsulCost) : (Number(data.kapsulCost) || 0),
                        kanatKapsulPrice: data.kanatKapsulPrice !== undefined ? Number(data.kanatKapsulPrice) : (Number(data.kapsulPrice) || 0),
                        kanatKapsulWholesale: data.kanatKapsulWholesale !== undefined ? Number(data.kanatKapsulWholesale) : (Number(data.kapsulWholesale) || 0)
                    }));
                }
            } catch (error) {
                console.error('Ayarlar yüklenirken hata:', error);
            }
        };
        fetchSettings();
        const fetchRates = async () => {
            try {
                const res = await fetch('https://open.er-api.com/v6/latest/USD');
                const data = await res.json();
                if (data && data.rates && data.rates.TRY && data.rates.EUR) {
                    const usdToTry = data.rates.TRY;
                    const eurToTry = data.rates.TRY / data.rates.EUR;
                    setExchangeRates({ USD: usdToTry.toFixed(2), EUR: eurToTry.toFixed(2) });
                }
            } catch (err) { }
        };
        fetchRates();
    }, []);
    const [newOrder, setNewOrder] = useState({
        roomName: '',
        productName: '',
        productVariant: '',
        width: '',
        height: '',
        pileModel: '',
        pileDensity: '',
        usedM2: '',
        unitPrice: '',
        extraPrice: '',
        capsuleUnitPrice: '',
        capsulePrice: '',
        jumboSystem: '',
        reductor: '',
        bottomSkirt: '',
        closedCase: '',
        laser: '',
        mounting: 0,
        motorType: '',
        motorPrice: '',
        motorPriceCurrency: '',
        isJumbo: false,
        hasAltEtekDilim: false,
        isKapaliKasa: false,
        // Ahşap Jaluzi
        sistemTipi: '',
        hasZigil: false,
        zigilAmount: 0,
        baseUnitPrice: '',
        // Metal Jaluzi
        hasMetalZigil: false,
        metalZigilAmount: 0,
        hasMetalReduktor: false,
        hasIkiCamArasi: false,
        ikiCamArasiAmount: 0,
        // Stor/Zebra Ek Özellikler
        direction: '',
        hasZebraJumbo: false,
        hasZebraAltEtekDilim: false,
        hasZebraKapaliKasa: false,
        zebraJumboAmount: 0,
        zebraAltEtekDilimAmount: 0,
        zebraKapaliKasaAmount: 0,
        // Stor Alanları
        hasStorJumbo: false,
        storJumboAmount: 0,
        hasStorAltEtekDilim: false,
        storAltEtekDilimAmount: 0,
        hasStorKapaliKasa: false,
        storKapaliKasaAmount: 0,
        hasStorAltEtekLazer: false,
        storAltEtekLazerAmount: 0,
        hasStorDekoratifLama: false,
        storDekoratifLamaAmount: 0,
        hasStorDekoratifKasa: false,
        storDekoratifKasaAmount: 0,
        hasStorAltEtekBeyazIs: false,
        storAltEtekBeyazIsAmount: 0,
        // Plise Özel
        pliseItems: [],
        pliseProduct1Name: '',
        pliseProduct1Price: '',
        pliseProduct2Name: '',
        pliseProduct2Price: '',
        pliseProduct1Variation: '',
        pliseProduct2Variation: '',
        pliseCaseVariation: '',
        // Maliyet Bilgileri
        unitCost: '',
        manualExtraCost: '',
        totalCost: '0.00',
        unitType: 'Adet',
        itemMeter: ''
    });
    // Siparişleri Firebase'den çek
    const fetchOrders = useCallback(async () => {
        if (!customerId || customerId.startsWith('temp_')) return;
        try {
            // Önce filtrele, sonra client-side'da sırala (index gerektirmez)
            const ordersQuery = query(
                collection(db, 'orders'),
                where('customerId', '==', customerId)
            );
            const querySnapshot = await getDocs(ordersQuery);
            const ordersData = querySnapshot.docs.map(doc => ({
                id: doc.id,
                ...doc.data()
            }));
            // Client-side'da tarihe göre sırala (en yeni en üstte)
            ordersData.sort((a, b) => {
                const dateA = a.createdAt?.toDate ? a.createdAt.toDate() : (a.createdAt ? new Date(a.createdAt) : new Date(0));
                const dateB = b.createdAt?.toDate ? b.createdAt.toDate() : (b.createdAt ? new Date(b.createdAt) : new Date(0));
                return dateB.getTime() - dateA.getTime(); // Descending order
            });
            setOrders(ordersData);
        } catch (error) {
            console.error('Siparişler yüklenirken hata:', error);
        }
    }, [customerId]);
    // Component mount olduğunda siparişleri çek
    useEffect(() => {
        fetchOrders();
    }, [fetchOrders]);
    // Pile modellerini sadece constants'tan al (Firestore'dan çekme)
    useEffect(() => {
        // Sadece constants'taki modelleri kullan - Tül ve Kanat için
        const tulModels = Object.entries(TUL_PILE_MODELS).map(([key, modelName]) => ({
            id: `const_tul_${key}`,
            modelName: modelName,
            density: calculatePileDensity(modelName)
        }));
        const kanatModels = Object.entries(KANAT_PILE_MODELS).map(([key, modelName]) => ({
            id: `const_kanat_${key}`,
            modelName: modelName,
            density: calculatePileDensity(modelName)
        }));
        // Her iki tipi birleştir (tekrarları kaldır)
        const allModels = [...tulModels];
        kanatModels.forEach(km => {
            if (!allModels.find(m => m.modelName === km.modelName)) {
                allModels.push(km);
            }
        });
        setPileModels(allModels);
    }, []);
    // Modal açıldığında edit-customer-section panel'ini yukarı scroll et
    useEffect(() => {
        if (deleteConfirm || cancelPaymentConfirm) {
            const panel = document.querySelector('.edit-customer-section');
            if (panel) {
                panel.scrollTo({ top: 0, behavior: 'smooth' });
            }
        }
    }, [deleteConfirm, cancelPaymentConfirm]);
    // Müşteri bilgilerinden vadeli yüzde ve genel iskonto bilgisini yükle
    useEffect(() => {
        if (customerInfo) {
            setVadeliYuzde(customerInfo.vadeliYuzde || '');
            setGenelIskontoYuzde(customerInfo.genelIskontoYuzde || '');
        }
    }, [customerInfo]);
    // Müşteri bilgilerinden ödeme bilgilerini yükle
    useEffect(() => {
        if (customerInfo) {
            // paidAmount number ise string'e çevir
            const paidAmountStr = customerInfo.paidAmount !== undefined && customerInfo.paidAmount !== null
                ? (typeof customerInfo.paidAmount === 'number' ? customerInfo.paidAmount.toString() : customerInfo.paidAmount.toString())
                : '';
            setPaidAmount(paidAmountStr);
            setIsPaymentCompleted(customerInfo.isPaymentCompleted || false);
            // Ödeme geçmişini yükle
            setPaymentHistory(customerInfo.paymentHistory || []);
        }
    }, [customerInfo]);
    // Müşteri/session değiştiğinde manuel toplam override'ı yükle
    useEffect(() => {
        if (customerInfo && customerInfo.manualTotalOverride != null) {
            setManualTotalOverride(parseFloat(customerInfo.manualTotalOverride));
        } else {
            setManualTotalOverride(null);
        }
        setIsEditingTotal(false);
    }, [customerId]);
    // Vadeli yüzde değiştiğinde parent'a bildir
    const handleVadeliYuzdeChange = (value) => {
        setVadeliYuzde(value);
        // Yeni müşteri ekleme modunda parent'a bildir
        if (onVadeliYuzdeChange) {
            onVadeliYuzdeChange(value);
        }
    };
    // Genel iskonto yüzde değiştiğinde parent'a bildir
    const handleGenelIskontoYuzdeChange = (value) => {
        setGenelIskontoYuzde(value);
        // Yeni müşteri ekleme modunda parent'a bildir
        if (onGenelIskontoYuzdeChange) {
            onGenelIskontoYuzdeChange(value);
        }
    };
    // Plise Batch Hesaplama Yardımcı Fonksiyonu
    const calculatePliseBatchTotals = (items, p1Price, p2Price, mounting, discount, globalQty = 1, extraPrice = 0, p1Cost = 0, p2Cost = 0, mountingCostValue = 0) => {
        const gQty = parseFloat(globalQty) || 1;
        const extra = parseFloat(extraPrice) || 0;
        const mCostVal = parseFloat(mountingCostValue) || 0;

        if (!Array.isArray(items) || items.length === 0) {
            const mount = (parseFloat(mounting) || 0) * gQty;
            const totalExtra = extra * gQty;
            return {
                items: items || [],
                totalPrice: (mount + totalExtra) > 0 ? (mount + totalExtra).toFixed(2) : '0.00',
                discountedPrice: null,
                totalCost: (mCostVal * gQty).toFixed(2)
            };
        }

        const p1 = parseFloat(p1Price) || 0;
        const p2 = parseFloat(p2Price) || 0;
        const c1 = parseFloat(p1Cost) || 0;
        const c2 = parseFloat(p2Cost) || 0;
        const mount = parseFloat(mounting) || 0;
        const disc = parseFloat(discount) || 0;
        let totalRowsSum = 0;
        let totalCostSum = 0;

        const updatedItems = items.map(item => {
            const m2 = calculatePliseUsedM2(item.width, item.height);
            const qty = parseFloat(item.quantity) || 1;

            // Fiyat hesabı
            const unitPrice = p1 + (item.multisistem ? p2 : 0);
            const rowTotal = m2 * unitPrice * qty;
            totalRowsSum += rowTotal;

            // Maliyet hesabı
            const unitCost = c1 + (item.multisistem ? c2 : 0);
            const rowCost = m2 * unitCost * qty;
            totalCostSum += rowCost;

            return {
                ...item,
                usedM2: m2.toFixed(2),
                rowTotal: rowTotal.toFixed(2)
            };
        });

        const finalTotal = (totalRowsSum + mount + extra) * gQty;
        const totalPrice = finalTotal.toFixed(2);
        const totalCost = ((totalCostSum + mCostVal) * gQty).toFixed(2);
        let discountedPrice = null;

        if (disc > 0) {
            discountedPrice = calculateDiscountedPrice(finalTotal, disc).toFixed(2);
        }

        return {
            items: updatedItems,
            totalPrice,
            discountedPrice,
            totalCost
        };
    };
    // Düzenleme modunda fiyat bilgilerini (vade/iskonto) kaydet
    const handleSavePricingFields = async () => {
        if (!customerId || customerId.startsWith('temp_')) {
            return;
        }
        try {
            setIsSavingVadeli(true);
            // Yeni vade/iskonto değerleriyle genel toplamı yeniden hesapla
            const newGrandTotal = calculateCustomerGrandTotal(
                { vadeliYuzde, genelIskontoYuzde },
                orders
            );
            const paid = parseFloat(paidAmount) || 0;
            const newRemaining = manualTotalOverride !== null
                ? (manualTotalOverride - paid)
                : (newGrandTotal - paid);
            const updateData = {
                vadeliYuzde: vadeliYuzde,
                genelIskontoYuzde: genelIskontoYuzde,
                updatedAt: new Date()
            };
            // Manuel override yoksa totalOrderAmount'ı da güncelle
            if (manualTotalOverride === null) {
                updateData.totalOrderAmount = newGrandTotal;
            }
            updateData.remainingAmount = newRemaining;
            await updateDoc(doc(db, parentCollectionName, customerId), updateData);
            // Başarılı kaydetme bildirimini parent'a gönder
            if (onSaveVadeliSuccess) {
                onSaveVadeliSuccess({
                    id: customerId,
                    vadeliYuzde,
                    genelIskontoYuzde,
                    totalOrderAmount: manualTotalOverride !== null ? manualTotalOverride : newGrandTotal,
                    remainingAmount: newRemaining
                });
            }
        } catch (error) {
            console.error('Fiyat bilgileri kaydedilirken hata:', error);
            showToast('Fiyat bilgileri kaydedilirken bir hata oluştu.');
        } finally {
            setIsSavingVadeli(false);
        }
    };
    // OrderStatus değiştiğinde combobox'ı güncelle
    useEffect(() => {
        // Bu useEffect orderStatus prop'u değiştiğinde çalışacak
        // Combobox otomatik olarak güncellenecek çünkü value={orderStatus} kullanıyoruz
    }, [orderStatus]);
    // Kopyalama sonrası hesaplamaları tetikle
    useEffect(() => {
        const currentIsWholesaleCtx = isWholesale || (customerInfo?.type === 'wholesale') || (customerInfo?.type === 'bayi');
        if (isAddingOrder && (selectedProductType === 'tul' || selectedProductType === 'kanat') && newOrder.pileModel && !currentIsWholesaleCtx) {
            // Kopyalama sonrası hesaplamaları yap
            const updatedOrder = { ...newOrder };
            // Pile sıklığını hesapla
            if (newOrder.pileModel) {
                // Önce Firestore'dan pile sıklığını al
                const selectedModel = pileModels.find(m => m.modelName === newOrder.pileModel);
                let pileDensity = 0;
                if (selectedModel) {
                    pileDensity = selectedModel.density;
                } else {
                    // Fallback: Eski calculatePileDensity fonksiyonunu kullan
                    pileDensity = calculatePileDensity(newOrder.pileModel);
                }
                updatedOrder.pileDensity = pileDensity.toString();
            }
            // Kullanılan mt2'yi hesapla
            if (newOrder.width && newOrder.height && newOrder.pileModel) {
                const usedM2 = calculateUsedM2(newOrder.width, newOrder.height, newOrder.pileModel);
                updatedOrder.usedM2 = usedM2.toFixed(2);
            }
            // Kapsül Pile için kapsül ücretini hesapla
            if (updatedOrder.pileModel === 'Kapsül Pile' && updatedOrder.usedM2 && updatedOrder.capsuleUnitPrice) {
                const capsulePrice = calculateCapsulePrice(updatedOrder.usedM2, updatedOrder.capsuleUnitPrice);
                updatedOrder.capsulePrice = capsulePrice.toFixed(2);
            }
            // Toplam tutarı hesapla - adet dahil et
            if (updatedOrder.usedM2 && updatedOrder.unitPrice) {
                const totalPrice = calculateTotalPrice(
                    updatedOrder.usedM2,
                    updatedOrder.unitPrice,
                    updatedOrder.extraPrice,
                    updatedOrder.pileModel,
                    updatedOrder.capsulePrice
                );
                // Adet ile çarp
                const quantity = parseFloat(updatedOrder.quantity) || 1;
                updatedOrder.totalPrice = (totalPrice * quantity).toFixed(2);
            }
            // Maliyet hesaplamasını güncelle
            const isKanat = (updatedOrder.productType || selectedProductType) === 'kanat';
            const extraCostValue = (updatedOrder.pileModel === "S Pile") ? (isKanat ? (adminSettings.kanatSPileCost !== undefined ? adminSettings.kanatSPileCost : (adminSettings.sPileCost || 0)) : (adminSettings.sPileCost || 0)) :
                (updatedOrder.pileModel === "Amerikan Pile 2'li") ? (isKanat ? (adminSettings.kanatAmerican2Cost !== undefined ? adminSettings.kanatAmerican2Cost : (adminSettings.american2Cost || 0)) : (adminSettings.american2Cost || 0)) :
                    (updatedOrder.pileModel === "Amerikan Pile 3'lü") ? (isKanat ? (adminSettings.kanatAmerican3Cost !== undefined ? adminSettings.kanatAmerican3Cost : (adminSettings.american3Cost || 0)) : (adminSettings.american3Cost || 0)) :
                        (updatedOrder.pileModel === "Kapsül Pile") ? (isKanat ? (adminSettings.kanatKapsulCost !== undefined ? adminSettings.kanatKapsulCost : (adminSettings.kapsulCost || 0)) : (adminSettings.kapsulCost || 0)) : 0;

            updatedOrder.unitCost = (Number(updatedOrder.baseUnitCost) || 0) + extraCostValue + (Number(updatedOrder.manualExtraCost) || 0);
            updatedOrder.totalCost = calculateTotalCost(updatedOrder.usedM2, updatedOrder.unitCost).toFixed(2);
            // İndirimli tutarı hesapla
            if (updatedOrder.totalPrice && updatedOrder.discount) {
                const discountedPrice = calculateDiscountedPrice(updatedOrder.totalPrice, updatedOrder.discount);
                updatedOrder.discountedPrice = discountedPrice.toFixed(2);
            }
            setNewOrder(updatedOrder);
        }
        // Ahşap Jaluzi ve Metal Jaluzi için kopyalama sonrası hesaplamaları tetikle
        if (isAddingOrder && (selectedProductType === 'ahsapJaluzi' || selectedProductType === 'metalJaluzi') && newOrder.width && newOrder.height) {
            // Kopyalama sonrası hesaplamaları yap
            const updatedOrder = { ...newOrder };
            // Kullanılan mt2'yi hesapla
            if (newOrder.width && newOrder.height) {
                const usedM2 = calculateJaluziUsedM2(newOrder.width, newOrder.height);
                updatedOrder.usedM2 = usedM2.toFixed(2);
            }
            // Toplam tutarı hesapla - adet dahil et
            if (updatedOrder.usedM2 && updatedOrder.unitPrice) {
                const totalPrice = calculateJaluziTotalPrice(
                    updatedOrder.usedM2,
                    updatedOrder.unitPrice,
                    updatedOrder.mounting,
                    updatedOrder.motorPrice,
                    (parseFloat(updatedOrder.zigilAmount) || 0) + (parseFloat(updatedOrder.metalZigilAmount) || 0),
                    parseFloat(updatedOrder.ikiCamArasiAmount) || 0
                );
                // Adet ile çarp
                const quantity = parseFloat(updatedOrder.quantity) || 1;
                updatedOrder.totalPrice = (totalPrice * quantity).toFixed(2);
            }
            // İndirimli tutarı hesapla
            if (updatedOrder.totalPrice && updatedOrder.discount) {
                const discountedPrice = calculateDiscountedPrice(updatedOrder.totalPrice, updatedOrder.discount);
                updatedOrder.discountedPrice = discountedPrice.toFixed(2);
            }
            setNewOrder(updatedOrder);
        }
        // Dikey Perde için kopyalama sonrası hesaplamaları tetikle
        if (isAddingOrder && selectedProductType === 'dikeyPerde' && newOrder.width && newOrder.height) {
            // Kopyalama sonrası hesaplamaları yap
            const updatedOrder = { ...newOrder };
            // Kullanılan mt2'yi hesapla
            if (newOrder.width && newOrder.height) {
                const usedM2 = calculateStorZebraUsedM2(newOrder.width, newOrder.height);
                updatedOrder.usedM2 = usedM2.toFixed(2);
            }
            // Toplam tutarı hesapla - adet dahil et
            if (updatedOrder.usedM2 && updatedOrder.unitPrice) {
                const totalPrice = calculateDikeyPerdeTotalPrice(
                    updatedOrder.usedM2,
                    updatedOrder.unitPrice,
                    0, // Fon ücreti zaten birim fiyata dahil ediliyor, burada 0 geçiyoruz
                    updatedOrder.mounting,
                    parseFloat(updatedOrder.motorPrice) || parseFloat(updatedOrder.motorPriceTL) || 0
                );
                // Adet ile çarp
                const quantity = parseFloat(updatedOrder.quantity) || 1;
                updatedOrder.totalPrice = (totalPrice * quantity).toFixed(2);
            }
            // İndirimli tutarı hesapla
            if (updatedOrder.totalPrice && updatedOrder.discount) {
                const discountedPrice = calculateDiscountedPrice(updatedOrder.totalPrice, updatedOrder.discount);
                updatedOrder.discountedPrice = discountedPrice.toFixed(2);
            }
            setNewOrder(updatedOrder);
        }
        // Dikey PVC ve Dikey Stor için kopyalama sonrası hesaplamaları tetikle
        if (isAddingOrder && (selectedProductType === 'dikeyPvc' || selectedProductType === 'dikeyStor') && newOrder.width && newOrder.height) {
            // Kopyalama sonrası hesaplamaları yap
            const updatedOrder = { ...newOrder };
            // Kullanılan mt2'yi hesapla
            if (newOrder.width && newOrder.height) {
                const usedM2 = calculateStorZebraUsedM2(newOrder.width, newOrder.height);
                updatedOrder.usedM2 = usedM2.toFixed(2);
            }
            // Toplam tutarı hesapla
            if (updatedOrder.usedM2 && updatedOrder.unitPrice) {
                const totalPrice = calculateJaluziTotalPrice(
                    updatedOrder.usedM2,
                    updatedOrder.unitPrice,
                    updatedOrder.mounting,
                    updatedOrder.motorPrice,
                    (parseFloat(updatedOrder.zigilAmount) || 0) + (parseFloat(updatedOrder.metalZigilAmount) || 0),
                    parseFloat(updatedOrder.ikiCamArasiAmount) || 0
                );
                // Adet ile çarp
                const quantity = parseFloat(updatedOrder.quantity) || 1;
                updatedOrder.totalPrice = (totalPrice * quantity).toFixed(2);
            }
            // İndirimli tutarı hesapla
            if (updatedOrder.totalPrice && updatedOrder.discount) {
                const discountedPrice = calculateDiscountedPrice(updatedOrder.totalPrice, updatedOrder.discount);
                updatedOrder.discountedPrice = discountedPrice.toFixed(2);
            }
            setNewOrder(updatedOrder);
        }
        // Plise Major Refactor - Kopyalama/Yükleme sonrası batch hesaplamaları tetikle
        if (isAddingOrder && selectedProductType === 'plise') {
            const { items, totalPrice, discountedPrice } = calculatePliseBatchTotals(
                newOrder.pliseItems,
                newOrder.pliseProduct1Price,
                newOrder.pliseProduct2Price,
                newOrder.mounting,
                newOrder.discount,
                newOrder.quantity,
                newOrder.extraPrice
            );
            // Sadece değerler gerçekten farklıysa state güncelle (Sonsuz döngüyü engellemek için)
            if (totalPrice !== newOrder.totalPrice || items.length !== (newOrder.pliseItems?.length || 0)) {
                setNewOrder(prev => ({
                    ...prev,
                    pliseItems: items,
                    totalPrice,
                    discountedPrice
                }));
            }
        }
        // Aksesuar için kopyalama sonrası hesaplamaları tetikle
        if (isAddingOrder && selectedProductType === 'aksesuar' && newOrder.unitPrice && newOrder.quantity) {
            // Kopyalama sonrası hesaplamaları yap
            const updatedOrder = { ...newOrder };
            // Toplam tutarı hesapla
            if (newOrder.unitPrice && newOrder.quantity) {
                const totalPrice = calculateAksesuarTotalPrice(
                    newOrder.unitPrice,
                    newOrder.quantity,
                    newOrder.unitType === 'Metre' ? newOrder.itemMeter : 0
                );
                updatedOrder.totalPrice = totalPrice.toFixed(2);
            }
            // İndirimli tutarı hesapla
            if (updatedOrder.totalPrice && updatedOrder.discount) {
                const discountedPrice = calculateDiscountedPrice(updatedOrder.totalPrice, updatedOrder.discount);
                updatedOrder.discountedPrice = discountedPrice.toFixed(2);
            }
            setNewOrder(updatedOrder);
        }
        // Katlamalı Perde için kopyalama sonrası hesaplamaları tetikle
        if (isAddingOrder && selectedProductType === 'katlamaliPerde' && newOrder.width) {
            const updatedOrder = { ...newOrder };
            const usedM = calculateKatlamaliPerdeUsedM(newOrder.width);
            updatedOrder.usedM2 = usedM.toFixed(2);
            if (updatedOrder.usedM2 && (updatedOrder.fabricPrice || updatedOrder.mechanismPrice)) {
                const baseTotal = calculateKatlamaliPerdeTotalPrice(
                    updatedOrder.usedM2,
                    updatedOrder.fabricPrice,
                    updatedOrder.mechanismPrice,
                    updatedOrder.accessoryPrice,
                    updatedOrder.mounting,
                    parseFloat(updatedOrder.motorPrice) || parseFloat(updatedOrder.motorPriceTL) || 0
                );
                const quantity = parseFloat(updatedOrder.quantity) || 1;
                updatedOrder.totalPrice = (baseTotal * quantity).toFixed(2);
            }
            if (updatedOrder.totalPrice && updatedOrder.discount) {
                const discountedPrice = calculateDiscountedPrice(updatedOrder.totalPrice, updatedOrder.discount);
                updatedOrder.discountedPrice = discountedPrice.toFixed(2);
            }
            setNewOrder(updatedOrder);
        }
    }, [isAddingOrder, selectedProductType, newOrder.pileModel, newOrder.width, newOrder.height, newOrder.unitPrice, newOrder.extraPrice, newOrder.pliseProduct1Price, newOrder.pliseProduct2Price, newOrder.pliseItems, newOrder.mounting, newOrder.discount, newOrder.manualExtraCost, newOrder.itemMeter, newOrder.unitType, adminSettings.american2Cost, adminSettings.american3Cost, adminSettings.kapsulCost, adminSettings.sPileCost, pileModels]);
    // Ürün tipi değiştiğinde alanları güncelle
    const handleProductTypeChange = (productType) => {
        setSelectedProductType(productType);
        setNewOrder({
            roomName: '',
            productName: '',
            productVariant: '',
            width: '',
            height: '',
            pileModel: '',
            pileDensity: '',
            usedM2: '',
            unitPrice: '',
            extraPrice: '',
            capsuleUnitPrice: '',
            capsulePrice: '',
            jumboSystem: '',
            reductor: '',
            bottomSkirt: '',
            closedCase: '',
            laser: '',
            mounting: '',
            backgroundCurtain: '',
            pliseProduct1Name: '',
            pliseProduct1Price: '',
            pliseProduct1Variation: '',
            pliseProduct2Name: '',
            pliseProduct2Price: '',
            pliseProduct2Variation: '',
            fabricPrice: '',
            mechanismType: '',
            mechanismPrice: '',
            accessoryPrice: '',
            quantity: '',
            discount: '',
            totalPrice: '',
            discountedPrice: '',
            notes: '',
            olcuTamamlama: '',
            usedMeter: '',
            // Stor Alanları
            hasStorJumbo: false,
            storJumboAmount: 0,
            hasStorAltEtekDilim: false,
            storAltEtekDilimAmount: 0,
            hasStorKapaliKasa: false,
            storKapaliKasaAmount: 0,
            hasStorAltEtekLazer: false,
            storAltEtekLazerAmount: 0,
            hasStorDekoratifLama: false,
            storDekoratifLamaAmount: 0,
            hasStorDekoratifKasa: false,
            storDekoratifKasaAmount: 0,
            hasStorAltEtekBeyazIs: false,
            storAltEtekBeyazIsAmount: 0,
            // Zebra Alanları
            hasZebraJumbo: false,
            zebraJumboAmount: 0,
            hasZebraAltEtekDilim: false,
            zebraAltEtekDilimAmount: 0,
            hasZebraKapaliKasa: false,
            zebraKapaliKasaAmount: 0,
            manualExtraCost: '',
            unitCost: '',
            totalCost: '0.00',
            unitType: 'Adet',
            itemMeter: '',
            // Ahşap Jaluzi
            sistemTipi: '',
            hasZigil: false,
            zigilAmount: 0,
            baseUnitPrice: '',
            // Metal Jaluzi
            hasMetalZigil: false,
            metalZigilAmount: 0,
            hasMetalReduktor: false,
            hasIkiCamArasi: false,
            ikiCamArasiAmount: 0,
            // Common / Generic
            direction: '',
            isJumbo: false,
            hasAltEtekDilim: false,
            isKapaliKasa: false,
            // Rustik Alanları
            usedCm: '',
            ayakModel: '',
            ayakCap: '',
            ayakUzunluk: '',
            ayakRenkKodu: '',
            ayakSayisi: '',
            ekAyakSayisi: '',
            ayakUcreti: '',
            ayakUcretiCost: '',
            ayakSayisiManual: false,
            rustikAksesuarlar: [],
            rustikRaySecenekleri: []
        });
    };
    // Alan değişikliklerinde hesaplamaları yap
    const handleFieldChange = useCallback((field, value) => {
        setNewOrder(prevOrder => {
            const updatedOrder = { ...prevOrder, [field]: value };

            // Motor seçimi Boş'a çekilirse motor ücretini ve kurunu sıfırla
            if (field === 'motorType' && !value) {
                updatedOrder.motorPrice = 0;
                updatedOrder.motorPriceCurrency = '';
            }
            // Montaj ücreti değiştiğinde maliyeti sıfırla
            if (field === 'mounting') {
                updatedOrder.mountingCost = '';
            }
            // Ayak ücreti değiştiğinde maliyeti sıfırla
            if (field === 'ayakUcreti') {
                updatedOrder.ayakUcretiCost = '';
            }
            // Ek ücret değiştiğinde maliyeti sıfırla
            if (field === 'extraPrice') {
                updatedOrder.manualExtraCost = '';
            }

            // 1. TÜL VE KANAT
            const isTulKanatWholesale = (selectedProductType === 'tul' || selectedProductType === 'kanat') &&
                (isWholesale || (customerInfo?.type === 'wholesale') || (customerInfo?.type === 'bayi'));
            if ((selectedProductType === 'tul' || selectedProductType === 'kanat') && !isTulKanatWholesale) {
                if (field === 'pileModel') {
                    let pileDensity = 0;
                    if (pileModels && pileModels.length > 0) {
                        const selectedModel = pileModels.find(m => m.modelName === value);
                        pileDensity = selectedModel?.density || calculatePileDensity(value);
                    } else {
                        pileDensity = calculatePileDensity(value);
                    }
                    updatedOrder.pileDensity = pileDensity.toString();
                    updatedOrder.pileModel = value;

                    const isKanat = selectedProductType === 'kanat';
                    const pileExtraCost = (value === "S Pile") ? (isKanat ? (adminSettings.kanatSPileCost !== undefined ? adminSettings.kanatSPileCost : (adminSettings.sPileCost || 0)) : (adminSettings.sPileCost || 0)) :
                        (value === "Amerikan Pile 2'li") ? (isKanat ? (adminSettings.kanatAmerican2Cost !== undefined ? adminSettings.kanatAmerican2Cost : (adminSettings.american2Cost || 0)) : (adminSettings.american2Cost || 0)) :
                            (value === "Amerikan Pile 3'lü") ? (isKanat ? (adminSettings.kanatAmerican3Cost !== undefined ? adminSettings.kanatAmerican3Cost : (adminSettings.american3Cost || 0)) : (adminSettings.american3Cost || 0)) :
                                (value === "Kapsül Pile") ? (isKanat ? (adminSettings.kanatKapsulCost !== undefined ? adminSettings.kanatKapsulCost : (adminSettings.kapsulCost || 0)) : (adminSettings.kapsulCost || 0)) : 0;

                    const rawFabric = parseFloat(updatedOrder.fabricCost) || 0;
                    const rawBase = parseFloat(prevOrder.baseUnitCost) || 0;
                    const baseCostForPile = rawFabric > 0 ? rawFabric : rawBase;

                    if (baseCostForPile > 0) {
                        updatedOrder.fabricCost = baseCostForPile;
                        updatedOrder.baseUnitCost = (baseCostForPile + pileExtraCost).toString();
                    }
                    if (updatedOrder.width && updatedOrder.height) {
                        updatedOrder.usedM2 = calculateUsedM2(updatedOrder.width, updatedOrder.height, value, pileDensity).toFixed(2);
                    }
                    if (value === 'Kapsül Pile') {
                        let capUnitPrice = 0;
                        if (isKanat) {
                            capUnitPrice = isWholesale
                                ? (adminSettings.kanatKapsulWholesale || adminSettings.kanatKapsulPrice || adminSettings.kapsulWholesale || adminSettings.kapsulPrice || 0)
                                : (adminSettings.kanatKapsulPrice || adminSettings.kapsulPrice || 0);
                        } else {
                            capUnitPrice = isWholesale ? (adminSettings.kapsulWholesale || adminSettings.kapsulPrice || 0) : (adminSettings.kapsulPrice || 0);
                        }
                        updatedOrder.capsuleUnitPrice = capUnitPrice.toString();
                        updatedOrder.capsulePrice = calculateCapsulePrice(updatedOrder.usedM2, capUnitPrice).toFixed(2);
                    } else {
                        updatedOrder.capsuleUnitPrice = '';
                        updatedOrder.capsulePrice = '';
                    }
                }

                if (field === 'pileDensity' && updatedOrder.pileModel && updatedOrder.width && updatedOrder.height) {
                    updatedOrder.usedM2 = calculateUsedM2(updatedOrder.width, updatedOrder.height, updatedOrder.pileModel, parseFloat(value) || 0).toFixed(2);
                    if (updatedOrder.pileModel === 'Kapsül Pile' && updatedOrder.capsuleUnitPrice) {
                        updatedOrder.capsulePrice = calculateCapsulePrice(updatedOrder.usedM2, updatedOrder.capsuleUnitPrice).toFixed(2);
                    }
                }

                if (['width', 'height'].includes(field)) {
                    updatedOrder.usedM2 = calculateUsedM2(updatedOrder.width, updatedOrder.height, updatedOrder.pileModel, parseFloat(updatedOrder.pileDensity) || 0).toFixed(2);
                    if (updatedOrder.pileModel === 'Kapsül Pile' && updatedOrder.capsuleUnitPrice) {
                        updatedOrder.capsulePrice = calculateCapsulePrice(updatedOrder.usedM2, updatedOrder.capsuleUnitPrice).toFixed(2);
                    }
                }
            }

            // 2. STOR VE ZEBRA
            if (selectedProductType === 'stor' || selectedProductType === 'zebra') {
                if (['width', 'height'].includes(field)) {
                    updatedOrder.usedM2 = calculateStorZebraUsedM2(updatedOrder.width, updatedOrder.height).toFixed(2);
                }
            }

            // 3. JALUZİ (AHŞAP & METAL)
            if (selectedProductType === 'ahsapJaluzi' || selectedProductType === 'metalJaluzi') {
                if (['width', 'height'].includes(field)) {
                    updatedOrder.usedM2 = calculateJaluziUsedM2(updatedOrder.width, updatedOrder.height).toFixed(2);
                }
            }

            // 4. DİKEY PERDE
            if (['dikeyPerde', 'dikeyPvc', 'dikeyStor'].includes(selectedProductType)) {
                if (['width', 'height'].includes(field)) {
                    updatedOrder.usedM2 = calculateStorZebraUsedM2(updatedOrder.width, updatedOrder.height).toFixed(2);
                }
            }

            // 5. PLİSE PERDE
            if (selectedProductType === 'plise') {
                const relevantFields = [
                    'pliseItems', 'pliseProduct1Price', 'pliseProduct2Price', 'mounting', 'discount',
                    'pliseProduct1Cost', 'pliseProduct2Cost', 'mountingCost', 'quantity', 'extraPrice'
                ];
                if (relevantFields.includes(field)) {
                    const { items, totalPrice, discountedPrice, totalCost } = calculatePliseBatchTotals(
                        updatedOrder.pliseItems,
                        updatedOrder.pliseProduct1Price,
                        updatedOrder.pliseProduct2Price,
                        updatedOrder.mounting,
                        updatedOrder.discount,
                        updatedOrder.quantity,
                        updatedOrder.extraPrice,
                        updatedOrder.pliseProduct1Cost,
                        updatedOrder.pliseProduct2Cost,
                        updatedOrder.mountingCost
                    );
                    updatedOrder.pliseItems = items;
                    updatedOrder.totalPrice = totalPrice;
                    updatedOrder.discountedPrice = discountedPrice;
                    updatedOrder.totalCost = totalCost;
                }
            }

            // 6. KATLAMALI PERDE
            if (selectedProductType === 'katlamaliPerde') {
                if (['width', 'height'].includes(field)) {
                    updatedOrder.usedM2 = calculateKatlamaliPerdeUsedM(updatedOrder.width).toFixed(2);
                }
            }

            // 7. AKSESUAR


            // 8. RAY SİSTEMİ
            if (selectedProductType === 'raySistemi') {
                if (field === 'width' || field === 'olcuTamamlama') {
                    const w = parseFloat(updatedOrder.width) || 0;
                    let meter = w / 100;
                    if (updatedOrder.olcuTamamlama === '25cm') meter = Math.ceil(w / 25) * 0.25;
                    else if (updatedOrder.olcuTamamlama === '50cm') meter = Math.ceil(w / 50) * 0.50;
                    updatedOrder.usedMeter = meter.toFixed(2);
                }
            }

            // 9. RUSTİK
            if (selectedProductType === 'rustik') {
                if (field === 'width') {
                    updatedOrder.usedCm = calculateRustikUsedCm(value);
                    updatedOrder.ayakSayisiManual = false;
                    updatedOrder.ayakSayisi = calculateRustikAyakSayisi(updatedOrder.usedCm);
                }
                if (field === 'ayakSayisi') {
                    updatedOrder.ayakSayisiManual = true;
                }

                const relevant = [
                    'width', 'usedCm', 'unitPrice', 'mounting', 'discount', 'quantity',
                    'ekAyakSayisi', 'ayakUcreti', 'ayakUcretiCost', 'rustikAksesuarlar', 'rustikRaySecenekleri',
                    'baseUnitCost', 'mountingCost', 'ayakSayisi'
                ];
                if (relevant.includes(field)) {
                    const { totalPrice, discountedPrice, totalCost } = calculateRustikTotals(updatedOrder);
                    updatedOrder.totalPrice = totalPrice;
                    updatedOrder.discountedPrice = discountedPrice;
                    updatedOrder.totalCost = totalCost;
                    updatedOrder.unitCost = updatedOrder.baseUnitCost || updatedOrder.unitCost || '';
                }
            }

            // --- GENEL HESAPLAMA BLOĞU ---
            const pricingFields = [
                'usedM2', 'unitPrice', 'extraPrice', 'quantity', 'mounting', 'motorPrice',
                'hasStorJumbo', 'hasStorAltEtekDilim', 'hasStorKapaliKasa', 'hasStorAltEtekLazer', 'hasStorDekoratifLama', 'hasStorDekoratifKasa', 'hasStorAltEtekBeyazIs',
                'hasZebraJumbo', 'hasZebraAltEtekDilim', 'hasZebraKapaliKasa',
                'hasZigil', 'hasMetalZigil', 'hasMetalReduktor', 'hasIkiCamArasi', 'hasDikeyFon',
                'storJumboAmount', 'storAltEtekDilimAmount', 'storKapaliKasaAmount', 'storAltEtekLazerAmount', 'storDekoratifLamaAmount', 'storDekoratifKasaAmount', 'storAltEtekBeyazIsAmount',
                'zebraJumboAmount', 'zebraAltEtekDilimAmount', 'zebraKapaliKasaAmount',
                'zigilAmount', 'metalZigilAmount', 'ikiCamArasiAmount', 'dikeyFonAmount', 'dikeyFonCount',
                'width', 'height', 'pileModel', 'usedMeter',
                'fabricPrice', 'mechanismPrice', 'accessoryPrice', 'manualExtraCost',
                'fabricCost', 'baseUnitCost', 'mountingCost', 'motorType'
            ];

            if (pricingFields.includes(field) && selectedProductType !== 'plise' && selectedProductType !== 'rustik') {
                const currentIsWholesale = isWholesale || (customerInfo?.type === 'wholesale') || (customerInfo?.type === 'bayi');
                const qty = parseFloat(updatedOrder.quantity) || 1;
                const motorCostTL = getMotorCostTL(updatedOrder.motorPrice, updatedOrder.motorPriceCurrency, exchangeRates);

                let baseTotal = 0;
                if ((selectedProductType === 'tul' || selectedProductType === 'kanat') && currentIsWholesale) {
                    // Toptan modunda: En(m) × Birim Fiyat (usedM2/pileModel hesabı yok)
                    const widthM = parseFloat(updatedOrder.width) || 0;
                    const uPrice = parseFloat(updatedOrder.unitPrice) || 0;
                    baseTotal = widthM * uPrice;
                } else if (selectedProductType === 'tul' || selectedProductType === 'kanat') {
                    baseTotal = calculateTotalPrice(updatedOrder.usedM2, updatedOrder.unitPrice, updatedOrder.extraPrice, updatedOrder.pileModel, updatedOrder.capsulePrice);
                } else if (selectedProductType === 'katlamaliPerde') {
                    baseTotal = calculateKatlamaliPerdeTotalPrice(updatedOrder.usedM2, updatedOrder.fabricPrice, updatedOrder.mechanismPrice, updatedOrder.accessoryPrice);
                } else if (selectedProductType === 'raySistemi' || selectedProductType === 'aksesuar') {
                    baseTotal = (parseFloat(updatedOrder.unitPrice) || 0) * (parseFloat(updatedOrder.usedMeter) || 1);
                } else {
                    // Stor, Zebra, Jaluzi, Dikey
                    const m2 = parseFloat(updatedOrder.usedM2) || 0;
                    const uPrice = parseFloat(updatedOrder.unitPrice) || 0;
                    const extra = parseFloat(updatedOrder.extraPrice) || 0;
                    const mounting = parseFloat(updatedOrder.mounting) || 0;

                    let systemExtras = 0;
                    let systemExtrasCostValue = 0;

                    // Stor ve Zebra için genişlik bazlı ekstra hesaplama (Min 1m, 0.5m katları)
                    if (selectedProductType === 'stor' || selectedProductType === 'zebra') {
                        const w = parseFloat(updatedOrder.width) || 0;
                        const roundedWidth = Math.max(1, Math.ceil(w / 50) * 0.5);
                        const isZebra = selectedProductType === 'zebra';
                        const prefix = isZebra ? 'Zebra' : 'Stor';
                        const settingsPrefix = isZebra ? 'zebra' : 'stor';
                        const extras = isZebra ? ['Jumbo', 'AltEtekDilim', 'KapaliKasa'] :
                            ['Jumbo', 'AltEtekDilim', 'KapaliKasa', 'AltEtekLazer', 'DekoratifLama', 'DekoratifKasa', 'AltEtekBeyazIs'];

                        extras.forEach(ext => {
                            const fieldKey = `has${prefix}${ext}`;
                            if (updatedOrder[fieldKey]) {
                                const priceKey = currentIsWholesale ? `${settingsPrefix}${ext}Wholesale` : `${settingsPrefix}${ext}Price`;
                                const costKey = `${settingsPrefix}${ext}Cost`;
                                const uPriceExt = parseFloat(adminSettings[priceKey] || adminSettings[`${settingsPrefix}${ext}Price`]) || 0;
                                const uCostExt = parseFloat(adminSettings[costKey]) || 0;

                                const amt = roundedWidth * uPriceExt;
                                const cst = roundedWidth * uCostExt;
                                systemExtras += amt;
                                systemExtrasCostValue += cst;
                                updatedOrder[`${settingsPrefix}${ext}Amount`] = amt.toFixed(2);
                            } else {
                                updatedOrder[`${settingsPrefix}${ext}Amount`] = 0;
                            }
                        });
                    } else {
                        // Jaluzi ve Dikey için sabit fiyatlı ekstralar (Amount alanlarını güncelle ve topla)
                        const otherExtras = [
                            { has: 'hasZigil', amt: 'zigilAmount', key: 'zigil' },
                            { has: 'hasMetalZigil', amt: 'metalZigilAmount', key: 'metalZigil' },
                            { has: 'hasIkiCamArasi', amt: 'ikiCamArasiAmount', key: 'ikiCamArasi' }
                        ];

                        otherExtras.forEach(ext => {
                            if (updatedOrder[ext.has]) {
                                const pKey = currentIsWholesale ? `${ext.key}Wholesale` : `${ext.key}Price`;
                                const cKey = `${ext.key}Cost`;
                                const uPriceExt = parseFloat(adminSettings[pKey] || adminSettings[`${ext.key}Price`]) || 0;
                                const uCostExt = parseFloat(adminSettings[cKey]) || 0;
                                const multiplier = ext.mult ? (parseInt(updatedOrder[ext.mult]) || 1) : 1;

                                const amt = uPriceExt * multiplier;
                                const cst = uCostExt * multiplier;

                                systemExtras += amt;
                                systemExtrasCostValue += cst;
                                updatedOrder[ext.amt] = amt.toFixed(2);
                            } else {
                                if (updatedOrder[ext.amt] !== undefined) updatedOrder[ext.amt] = 0;
                            }
                        });

                        // Dikey Fon miktarını sadece bilgi amaçlı güncelleyelim (Sistem ekstralarına eklemiyoruz çünkü birim fiyata dahil)
                        if (updatedOrder.hasDikeyFon) {
                            const pKey = currentIsWholesale ? `dikeyFonWholesale` : `dikeyFonPrice`;
                            const uPriceExt = parseFloat(adminSettings[pKey] || adminSettings.dikeyFonPrice) || 0;
                            updatedOrder.dikeyFonAmount = uPriceExt.toFixed(2);
                        } else {
                            updatedOrder.dikeyFonAmount = 0;
                        }
                    }

                    baseTotal = (m2 * (uPrice + extra)) + systemExtras + mounting;
                    updatedOrder.systemExtrasCostValue = systemExtrasCostValue; // Maliyet bloğunda kullanmak için
                }

                updatedOrder.totalPrice = ((baseTotal + motorCostTL) * qty).toFixed(2);
                updatedOrder.discountedPrice = calculateDiscountedPrice(updatedOrder.totalPrice, updatedOrder.discount).toFixed(2);


                // MALİYET HESABI
                const fCost = parseFloat(updatedOrder.fabricCost) || 0;
                const bCost = parseFloat(updatedOrder.baseUnitCost) || fCost;
                const mCostValue = parseFloat(updatedOrder.mountingCost) || 0;
                const hasMotor = !!updatedOrder.motorType;
                const sExtrasCost = parseFloat(updatedOrder.systemExtrasCostValue) || 0;

                // Maliyet kalemlerinden biri varsa veya ürün tipine göre hesaplama gerekiyorsa
                if (bCost > 0 || mCostValue > 0 || hasMotor || sExtrasCost > 0 || ['stor', 'zebra', 'ahsapJaluzi', 'metalJaluzi', 'plise'].includes(selectedProductType)) {
                    const mExtra = parseFloat(updatedOrder.manualExtraCost) || 0;

                    let extraUnitCost = 0;
                    if (selectedProductType === 'metalJaluzi' && updatedOrder.hasMetalReduktor) {
                        extraUnitCost = parseFloat(adminSettings.metalReduktorCost) || 0;
                    }
                    if (['dikeyPerde', 'dikeyPvc', 'dikeyStor'].includes(selectedProductType) && updatedOrder.hasDikeyFon) {
                        extraUnitCost += parseFloat(adminSettings.dikeyFonCost) || 0;
                    }

                    updatedOrder.unitCost = bCost + mExtra + extraUnitCost;
                    const currentIsWholesale = isWholesale || (customerInfo?.type === 'wholesale') || (customerInfo?.type === 'bayi');
                    let measure = parseFloat(updatedOrder.usedM2) || parseFloat(updatedOrder.usedMeter) || 0;
                    if ((selectedProductType === 'tul' || selectedProductType === 'kanat') && currentIsWholesale) {
                        measure = parseFloat(updatedOrder.width) || parseFloat(updatedOrder.usedM2) || 0;
                    } else if (!measure && (selectedProductType === 'raySistemi' || selectedProductType === 'aksesuar')) {
                        measure = 1;
                    } else if (!measure) {
                        measure = parseFloat(updatedOrder.usedM2) || parseFloat(updatedOrder.width) || 1;
                    }

                    // Ürün tipine göre motor maliyetini bul (Sadece motor seçilmişse)
                    let motorCostValue = 0;
                    const motorEnabledTypes = ['ahsapJaluzi', 'metalJaluzi', 'stor', 'zebra', 'dikeyPerde', 'dikeyPvc', 'dikeyStor', 'katlamaliPerde'];
                    if (motorEnabledTypes.includes(selectedProductType) && updatedOrder.motorType) {
                        const settingsKey = `${selectedProductType}MotorCost`;
                        const cost = adminSettings[settingsKey] || 0;
                        const currencyKey = `${selectedProductType}MotorCurrency`;
                        const currency = adminSettings[currencyKey] || 'USD';

                        if (currency === 'TL' || currency === 'TRY') {
                            motorCostValue = cost;
                        } else {
                            const specialRateKey = isWholesale ? `${selectedProductType}MotorSpecialRateWholesale` : `${selectedProductType}MotorSpecialRateRetail`;
                            const specialRate = adminSettings[specialRateKey] || 0;
                            const rate = specialRate !== 0 ? specialRate : (exchangeRates[currency] || 1);
                            motorCostValue = cost * rate;
                        }
                    }

                    const mountCost = parseFloat(updatedOrder.mountingCost) || 0;
                    const sExtrasCost = parseFloat(updatedOrder.systemExtrasCostValue) || 0;

                    // Kapsül Pile maliyeti
                    let capsuleCostValue = 0;
                    if (selectedProductType === 'tul' || selectedProductType === 'kanat') {
                        if (updatedOrder.pileModel === 'Kapsül Pile') {
                            const roundedM2 = Math.ceil(measure);
                            const kapsulCostRate = selectedProductType === 'kanat' ? (adminSettings.kanatKapsulCost !== undefined ? adminSettings.kanatKapsulCost : adminSettings.kapsulCost) : adminSettings.kapsulCost;
                            capsuleCostValue = roundedM2 * 7 * (parseFloat(kapsulCostRate) || 0);
                        }
                    }

                    // Toplam Maliyet = ((Metraj * Birim Maliyet) + Sistem Ekstraları Maliyeti + Motor Maliyeti + Montaj Maliyeti + Kapsül Maliyeti) * Adet
                    updatedOrder.totalCost = (((measure * updatedOrder.unitCost) + sExtrasCost + motorCostValue + mountCost + capsuleCostValue) * qty).toFixed(2);
                } else {
                    updatedOrder.unitCost = 0;
                    updatedOrder.totalCost = '0.00';
                }
            }

            return updatedOrder;
        });
    }, [selectedProductType, isWholesale, customerInfo, exchangeRates, adminSettings, pileModels, isAddingOrder]);
    // Yeni sipariş ekle veya mevcut siparişi güncelle
    const handleAddOrder = async (e, forceSave = false) => {
        if (e) e.preventDefault();
        // Stor/Zebra için ek özellik kontrolü
        if (!forceSave && (selectedProductType === 'stor' || selectedProductType === 'zebra')) {
            const isZebra = selectedProductType === 'zebra';
            const hasAnyExtra = isZebra ?
                (newOrder.hasZebraJumbo || newOrder.hasZebraAltEtekDilim || newOrder.hasZebraKapaliKasa) :
                (newOrder.hasStorJumbo || newOrder.hasStorAltEtekDilim || newOrder.hasStorKapaliKasa ||
                    newOrder.hasStorAltEtekLazer || newOrder.hasStorDekoratifLama ||
                    newOrder.hasStorDekoratifKasa || newOrder.hasStorAltEtekBeyazIs);
            if (!hasAnyExtra) {
                setShowSystemConfirm(true);
                return;
            }
        }
        // customerId kontrolü
        if (!customerId) {
            showToast('Müşteri ID bulunamadı!');
            return;
        }
        // Geçici ID kontrolü (temp_ ile başlıyorsa local state'e ekle)
        if (customerId.startsWith('temp_')) {
            const tempOrder = {
                id: editingOrderId || Date.now(),
                customerId: customerId,
                productType: selectedProductType,
                ...newOrder,
                createdAt: new Date(),
                isTemp: true // Geçici sipariş işareti
            };
            if (editingOrderId) {
                // Edit modu - mevcut siparişi güncelle
                setOrders(orders.map(order =>
                    order.id === editingOrderId ? tempOrder : order
                ));
            } else {
                // Yeni sipariş ekle
                setOrders([...orders, tempOrder]);
            }
            setIsAddingOrder(false);
            setEditingOrderId(null);
            setNewOrder({
                roomName: '',
                productName: '',
                productVariant: '',
                width: '',
                height: '',
                pileModel: '',
                pileDensity: '',
                usedM2: '',
                unitPrice: '',
                extraPrice: '',
                capsuleUnitPrice: '',
                capsulePrice: '',
                jumboSystem: '',
                reductor: '',
                bottomSkirt: '',
                closedCase: '',
                laser: '',
                mounting: 0,
                pliseItems: [],
                pliseProduct1Name: '',
                pliseProduct1Price: '',
                pliseProduct2Name: '',
                pliseProduct2Price: '',
                pliseProduct1Variation: '',
                pliseProduct2Variation: '',
                pliseCaseVariation: '',
                backgroundCurtain: '',
                blackout: '',
                crepTul: '',
                multisistem: '',
                honeycomb: '',
                standartPlise: '',
                fabricPrice: '',
                mechanismType: '',
                mechanismPrice: '',
                accessoryPrice: '',
                quantity: '',
                discount: '',
                totalPrice: '',
                discountedPrice: '',
                notes: '',
                direction: '',
                hasZebraJumbo: false,
                hasZebraAltEtekDilim: false,
                hasZebraKapaliKasa: false,
                zebraJumboAmount: 0,
                zebraAltEtekDilimAmount: 0,
                zebraKapaliKasaAmount: 0
            });
            setSelectedProductType('');
            return;
        }
        // Tül ve Kanat için gerekli alanları kontrol et
        if (selectedProductType === 'tul' || selectedProductType === 'kanat') {
            const tulKanatIsWholesale = isWholesale || (customerInfo?.type === 'wholesale') || (customerInfo?.type === 'bayi');
            if (tulKanatIsWholesale) {
                // Toptan modunda: Ürün Adı, En (m), Birim Fiyat, Adet zorunlu
                if (!newOrder.productName || !newOrder.width || !newOrder.unitPrice || !newOrder.quantity) {
                    showToast('Lütfen zorunlu alanları doldurun! (Ürün Adı, En (m), Birim Fiyat, Adet)');
                    return;
                }
            } else {
                // Perakende modunda: tüm alanlar zorunlu
                if (!newOrder.roomName || !newOrder.productName || !newOrder.width || !newOrder.height || !newOrder.pileModel || !newOrder.unitPrice) {
                    showToast('Lütfen tüm zorunlu alanları doldurun!');
                    return;
                }
            }
        } else if (selectedProductType === 'stor' || selectedProductType === 'zebra') {
            // Stor ve Zebra için gerekli alanları kontrol et
            if ((!isWholesale && !newOrder.roomName) || !newOrder.productName || !newOrder.width || !newOrder.height || !newOrder.unitPrice || !newOrder.direction) {
                showToast('Lütfen tüm zorunlu alanları doldurun! (Yön seçimi dahil)');
                return;
            }
        } else if (selectedProductType === 'ahsapJaluzi' || selectedProductType === 'metalJaluzi') {
            // Ahşap Jaluzi ve Metal Jaluzi için gerekli alanları kontrol et
            if ((!isWholesale && !newOrder.roomName) || !newOrder.productName || !newOrder.width || !newOrder.height || !newOrder.unitPrice || !newOrder.direction) {
                showToast('Lütfen tüm zorunlu alanları doldurun! (Yön seçimi dahil)');
                return;
            }
        } else if (selectedProductType === 'dikeyPerde' || selectedProductType === 'dikeyPvc' || selectedProductType === 'dikeyStor') {
            // Dikey ürünler için gerekli alanları kontrol et
            if ((!isWholesale && !newOrder.roomName) || !newOrder.productName || !newOrder.width || !newOrder.height || !newOrder.unitPrice || !newOrder.direction) {
                showToast('Lütfen tüm zorunlu alanları doldurun! (Yön seçimi dahil)');
                return;
            }
            // Fon seçiliyse, Fon Adı ve Fon Sayısı zorunludur ve sayı geçerli olmalıdır
            if (newOrder.hasDikeyFon) {
                if (!newOrder.dikeyFonName || String(newOrder.dikeyFonName).trim() === '') {
                    showToast('Lütfen Fon Adı alanını doldurun!');
                    return;
                }
                if (!newOrder.dikeyFonCount || parseInt(newOrder.dikeyFonCount) <= 0) {
                    showToast('Fon Sayısı en az 1 olmalıdır!');
                    return;
                }
            }
        } else if (selectedProductType === 'plise') {
            // Plise için gerekli alanları kontrol et
            if ((!isWholesale && !newOrder.roomName) || !newOrder.pliseProduct1Name || !newOrder.pliseProduct1Price) {
                showToast('Lütfen Oda Adı, 1. Ürün Adı ve 1. Ürün Fiyatı alanlarını doldurun!');
                return;
            }
            if (!Array.isArray(newOrder.pliseItems) || newOrder.pliseItems.length === 0) {
                showToast('Lütfen en az bir ölçü ekleyin!');
                return;
            }
            // Her satırı kontrol et
            const invalidItem = newOrder.pliseItems.find(item => !item.width || !item.height || !item.quantity);
            if (invalidItem) {
                showToast('Lütfen listedeki tüm ölçü ve adet alanlarını doldurun!');
                return;
            }
        } else if (selectedProductType === 'katlamaliPerde') {
            // Katlamalı Perde için gerekli alanları kontrol et
            if ((!isWholesale && !newOrder.roomName) || !newOrder.productName || !newOrder.width || !newOrder.height || !newOrder.fabricPrice || !newOrder.mechanismType || !newOrder.mechanismPrice) {
                showToast('Lütfen tüm zorunlu alanları doldurun!');
                return;
            }
        } else if (selectedProductType === 'aksesuar') {
            // Aksesuar için gerekli alanları kontrol et
            if ((!isWholesale && !newOrder.roomName) || !newOrder.productName || !newOrder.unitPrice || !newOrder.quantity) {
                showToast('Lütfen tüm zorunlu alanları doldurun!');
                return;
            }
        } else if (selectedProductType === 'rustik') {
            // Rustik için gerekli alanları kontrol et
            if ((!isWholesale && !newOrder.roomName) || !newOrder.productName || !newOrder.width || !newOrder.unitPrice ||
                !newOrder.ayakModel || !newOrder.ayakCap || !newOrder.ayakUzunluk || !newOrder.ayakRenkKodu || !newOrder.ayakSayisi) {
                showToast('Lütfen tüm zorunlu alanları doldurun!');
                return;
            }
        } else {
            // Diğer ürün tipleri için genel kontrol
            if (!newOrder.productName) {
                showToast('Lütfen tüm zorunlu alanları doldurun!');
                return;
            }
        }
        // undefined değerleri temizle (Firebase undefined kabul etmez)
        const cleanOrder = Object.entries(newOrder).reduce((acc, [key, value]) => {
            if (value !== undefined) {
                acc[key] = value;
            }
            return acc;
        }, {});
        const order = {
            customerId: customerId, // Müşteri ID'sini ekle
            productType: selectedProductType,
            ...cleanOrder
        };
        // Yeni sipariş için createdAt ekle (düzenlemede ekleme)
        if (!editingOrderId) {
            order.createdAt = new Date();
        }
        // Tül/Kanat için totalCost'u kayıt öncesi yeniden hesapla (adet dahil)
        // Bu, kopyalama ve düzenleme durumlarında eski/eksik değerlerin yazılmasını engeller
        if (selectedProductType === 'tul' || selectedProductType === 'kanat') {
            const fabricCost = parseFloat(order.fabricCost) || 0;
            const baseUnitCost = parseFloat(order.baseUnitCost) || fabricCost;
            const manualExtra = parseFloat(order.manualExtraCost) || 0;
            const finalUnitCost = baseUnitCost + manualExtra;
            const currentIsWholesale = isWholesale || (customerInfo?.type === 'wholesale') || (customerInfo?.type === 'bayi');
            const measure = currentIsWholesale
                ? (parseFloat(order.width) || parseFloat(order.usedM2) || 0)
                : (parseFloat(order.usedM2) || parseFloat(order.width) || 0);
            const qty = parseFloat(order.quantity) || 1;

            // Ek maliyetleri topla
            const mCost = parseFloat(order.mountingCost) || 0;
            const sExtrasCost = parseFloat(order.systemExtrasCostValue) || 0;

            // Motor maliyeti
            let motorCostValue = 0;
            if (order.motorType) {
                const settingsKey = `${selectedProductType}MotorCost`;
                const cost = adminSettings[settingsKey] || 0;
                const currencyKey = `${selectedProductType}MotorCurrency`;
                const currency = adminSettings[currencyKey] || 'USD';
                const rate = exchangeRates[currency] || 1;
                motorCostValue = cost * rate;
            }

            // Kapsül maliyeti
            let capsuleCostValue = 0;
            if (order.pileModel === 'Kapsül Pile') {
                const roundedM2 = Math.ceil(measure);
                const kapsulCostRate = selectedProductType === 'kanat' ? (adminSettings.kanatKapsulCost !== undefined ? adminSettings.kanatKapsulCost : adminSettings.kapsulCost) : adminSettings.kapsulCost;
                capsuleCostValue = roundedM2 * 7 * (parseFloat(kapsulCostRate) || 0);
            }

            order.unitCost = finalUnitCost;
            order.totalCost = (((measure * finalUnitCost) + sExtrasCost + motorCostValue + mCost + capsuleCostValue) * qty).toFixed(2);
        }
        if (selectedProductType === 'rustik') {
            const { totalPrice, discountedPrice, totalCost } = calculateRustikTotals(order);
            order.totalPrice = totalPrice;
            order.discountedPrice = discountedPrice;
            order.totalCost = totalCost;
            order.unitCost = order.baseUnitCost || order.unitCost || '';
            order.rustikRaySecenekleri = sanitizeRustikExtrasForSave(order.rustikRaySecenekleri);
            order.rustikAksesuarlar = sanitizeRustikExtrasForSave(order.rustikAksesuarlar);
        }
        try {
            if (editingOrderId) {
                // Edit modu - mevcut siparişi güncelle
                await updateDoc(doc(db, 'orders', editingOrderId), order);
            } else {
                // Yeni sipariş ekle
                await addDoc(collection(db, 'orders'), order);
            }
            fetchOrders(); // Sipariş listesini yeniden çek
            // Parent döküman (dealer_sessions vs) bakiyesini güncelle
            if (onOrderSaved && customerId && !customerId.startsWith('temp_')) {
                // Güncel sipariş listesini kullanarak hesapla
                // fetchOrders async olduğu için kısa timeout ile state'i bekle
                setTimeout(() => {
                    if (onOrderSaved) onOrderSaved();
                }, 500);
            }
            setIsAddingOrder(false);
            setEditingOrderId(null);
            setNewOrder({
                roomName: '',
                productName: '',
                productVariant: '',
                width: '',
                height: '',
                pileModel: '',
                pileDensity: '',
                usedM2: '',
                unitPrice: '',
                extraPrice: '',
                capsuleUnitPrice: '',
                capsulePrice: '',
                jumboSystem: '',
                reductor: '',
                bottomSkirt: '',
                closedCase: '',
                laser: '',
                mounting: 0,
                pliseItems: [],
                pliseProduct1Name: '',
                pliseProduct1Price: '',
                pliseProduct2Name: '',
                pliseProduct2Price: '',
                pliseProduct1Variation: '',
                pliseProduct2Variation: '',
                pliseCaseVariation: '',
                backgroundCurtain: '',
                blackout: '',
                crepTul: '',
                multisistem: '',
                honeycomb: '',
                standartPlise: '',
                fabricPrice: '',
                mechanismType: '',
                mechanismPrice: '',
                accessoryPrice: '',
                quantity: '',
                discount: '',
                totalPrice: '',
                discountedPrice: '',
                notes: '',
                direction: '',
                hasZebraJumbo: false,
                hasZebraAltEtekDilim: false,
                hasZebraKapaliKasa: false,
                zebraJumboAmount: 0,
                zebraAltEtekDilimAmount: 0,
                zebraKapaliKasaAmount: 0
            });
            setSelectedProductType('');
            // Ekranı yukarı scroll et (mobilde input alanları alt alta olduğu için)
            setTimeout(() => {
                window.scrollTo({
                    top: 0,
                    behavior: 'smooth'
                });
            }, 100);
        } catch (error) {
            console.error('Sipariş eklenirken hata:', error);
            showToast('Sipariş eklenirken bir hata oluştu: ' + error.message);
        }
    };
    // Sipariş silme onayı
    const handleDeleteConfirm = (order) => {
        setDeleteConfirm(order);
    };
    // Sipariş sil
    const handleDeleteOrder = async () => {
        if (!deleteConfirm) return;
        try {
            await deleteDoc(doc(db, 'orders', deleteConfirm.id));
            setDeleteConfirm(null);
            fetchOrders(); // Sipariş silindikten sonra listeyi yeniden çek
            // Parent döküman bakiyesini güncelle
            if (onOrderSaved && customerId && !customerId.startsWith('temp_')) {
                setTimeout(() => {
                    if (onOrderSaved) onOrderSaved();
                }, 500);
            }
        } catch (error) {
            console.error('Sipariş silinirken hata:', error);
            showToast('Sipariş silinirken bir hata oluştu.');
        }
    };
    // Sipariş düzenle
    // Sipariş düzenle
    const handleEditOrder = (order) => {
        setSelectedProductType(order.productType);
        // Plise için geriye dönük uyumluluk dönüşümü
        let pliseItems = order.pliseItems || [];
        if (order.productType === 'plise' && (!pliseItems || pliseItems.length === 0) && order.width && order.height) {
            pliseItems = [{
                id: Date.now(),
                width: order.width,
                height: order.height,
                usedM2: order.usedM2 || '1.00',
                quantity: order.quantity || '1',
                multisistem: order.multisistem || false,
                rowTotal: order.totalPrice || '0.00'
            }];
        }
        const initialOrder = {
            roomName: order.roomName,
            productName: order.productName,
            productVariant: order.productVariant || '',
            width: order.width,
            height: order.height,
            pileModel: order.pileModel,
            pileDensity: order.pileDensity,
            usedM2: order.usedM2,
            usedCm: order.usedCm,
            usedMeter: order.usedMeter,
            olcuTamamlama: order.olcuTamamlama,
            unitPrice: order.unitPrice,
            extraPrice: order.extraPrice,
            capsuleUnitPrice: order.capsuleUnitPrice,
            capsulePrice: order.capsulePrice,
            jumboSystem: order.jumboSystem,
            reductor: order.reductor,
            bottomSkirt: order.bottomSkirt,
            closedCase: order.closedCase,
            laser: order.laser,
            mounting: order.mounting,
            backgroundCurtain: order.backgroundCurtain,
            blackout: order.blackout,
            crepTul: order.crepTul,
            multisistem: order.multisistem,
            honeycomb: order.honeycomb,
            standartPlise: order.standartPlise,
            fabricPrice: order.fabricPrice,
            mechanismType: order.mechanismType,
            mechanismPrice: order.mechanismPrice,
            accessoryPrice: order.accessoryPrice,
            accessories: order.accessories || [],
            rustikAksesuarlar: cloneRustikExtras(order.rustikAksesuarlar),
            rustikRaySecenekleri: cloneRustikExtras(order.rustikRaySecenekleri),
            ayakModel: order.ayakModel || '',
            ayakCap: order.ayakCap || '',
            ayakUzunluk: order.ayakUzunluk || '',
            ayakRenkKodu: order.ayakRenkKodu || '',
            ayakSayisi: order.ayakSayisi || '',
            ekAyakSayisi: order.ekAyakSayisi || '',
            ayakUcreti: order.ayakUcreti || 0,
            ayakUcretiCost: order.ayakUcretiCost || '',
            ayakSayisiManual: order.ayakSayisiManual || (
                String(order.ayakSayisi || '') !== String(calculateRustikAyakSayisi(order.usedCm))
            ),
            quantity: order.quantity,
            discount: order.discount,
            totalPrice: order.totalPrice,
            discountedPrice: order.discountedPrice,
            unitCost: order.unitCost || '',
            baseUnitCost: order.baseUnitCost || '',
            totalCost: order.totalCost || '0.00',
            fabricCost: order.fabricCost || '',
            mountingCost: order.mountingCost || '',
            notes: order.notes,
            motorType: order.motorType || '',
            motorPrice: order.motorPrice || 0,
            manualExtraCost: order.manualExtraCost || '',
            unitType: order.unitType || 'Adet',
            itemMeter: order.itemMeter || '',
            motorPriceCurrency: order.motorPriceCurrency || '',
            direction: order.direction || '',
            // Plise Özel
            pliseItems: pliseItems,
            pliseProduct1Name: order.pliseProduct1Name || (order.productType === 'plise' ? order.productName : ''),
            pliseProduct1Price: order.pliseProduct1Price || (order.productType === 'plise' ? (order.unitPrice || 0) : 0),
            pliseProduct2Name: order.pliseProduct2Name || '',
            pliseProduct2Price: order.pliseProduct2Price || 0,
            pliseProduct1Cost: order.pliseProduct1Cost || 0,
            pliseProduct2Cost: order.pliseProduct2Cost || 0,
            pliseProduct1Variation: order.pliseProduct1Variation || '',
            pliseProduct2Variation: order.pliseProduct2Variation || '',
            pliseCaseVariation: order.pliseCaseVariation || '',
            hasStorJumbo: order.hasStorJumbo || false,
            hasStorAltEtekDilim: order.hasStorAltEtekDilim || false,
            hasStorKapaliKasa: order.hasStorKapaliKasa || false,
            hasStorAltEtekLazer: order.hasStorAltEtekLazer || false,
            hasStorDekoratifLama: order.hasStorDekoratifLama || false,
            hasStorDekoratifKasa: order.hasStorDekoratifKasa || false,
            hasStorAltEtekBeyazIs: order.hasStorAltEtekBeyazIs || false,
            storJumboAmount: order.storJumboAmount || 0,
            storAltEtekDilimAmount: order.storAltEtekDilimAmount || 0,
            storKapaliKasaAmount: order.storKapaliKasaAmount || 0,
            storAltEtekLazerAmount: order.storAltEtekLazerAmount || 0,
            storDekoratifLamaAmount: order.storDekoratifLamaAmount || 0,
            storDekoratifKasaAmount: order.storDekoratifKasaAmount || 0,
            storAltEtekBeyazIsAmount: order.storAltEtekBeyazIsAmount || 0,
            hasZebraJumbo: order.hasZebraJumbo || false,
            hasZebraAltEtekDilim: order.hasZebraAltEtekDilim || false,
            hasZebraKapaliKasa: order.hasZebraKapaliKasa || false,
            zebraJumboAmount: order.zebraJumboAmount || 0,
            zebraAltEtekDilimAmount: order.zebraAltEtekDilimAmount || 0,
            zebraKapaliKasaAmount: order.zebraKapaliKasaAmount || 0,
            hasDikeyFon: order.hasDikeyFon || false,
            dikeyFonName: order.dikeyFonName || '',
            dikeyFonCount: order.dikeyFonCount || 1,
            dikeyFonAmount: order.dikeyFonAmount || 0,
            // Ahşap Jaluzi
            sistemTipi: order.sistemTipi || '',
            hasZigil: order.hasZigil || false,
            zigilAmount: order.zigilAmount || 0,
            hasReduktor: order.hasReduktor || false,
            hasKurdela: order.hasKurdela || false,
            baseUnitPrice: order.baseUnitPrice || '',
            // Metal Jaluzi
            hasMetalZigil: order.hasMetalZigil || false,
            metalZigilAmount: order.metalZigilAmount || 0,
            hasMetalReduktor: order.hasMetalReduktor || false,
            hasIkiCamArasi: order.hasIkiCamArasi || false,
            ikiCamArasiAmount: order.ikiCamArasiAmount || 0
        };
        // Plise için hesaplamaları önden yap (maliyet dahil — bozulan totalCost'u da düzelt)
        if (order.productType === 'plise') {
            const { items, totalPrice, discountedPrice, totalCost } = calculatePliseBatchTotals(
                pliseItems,
                initialOrder.pliseProduct1Price,
                initialOrder.pliseProduct2Price,
                initialOrder.mounting,
                initialOrder.discount,
                initialOrder.quantity,
                initialOrder.extraPrice,
                initialOrder.pliseProduct1Cost,
                initialOrder.pliseProduct2Cost,
                initialOrder.mountingCost
            );
            initialOrder.pliseItems = items;
            initialOrder.totalPrice = totalPrice;
            initialOrder.discountedPrice = discountedPrice;
            initialOrder.totalCost = totalCost;
        }
        if (order.productType === 'rustik') {
            if (order.width) {
                initialOrder.usedCm = initialOrder.usedCm || calculateRustikUsedCm(order.width);
            }
            const { totalPrice, discountedPrice, totalCost } = calculateRustikTotals(initialOrder);
            initialOrder.totalPrice = totalPrice;
            initialOrder.discountedPrice = discountedPrice;
            initialOrder.totalCost = totalCost;
        }
        setNewOrder(initialOrder);
        setIsAddingOrder(true);
        setEditingOrderId(order.id);
        if (order.productType === 'rustik') {
            setRustikFormSessionKey((k) => k + 1);
        }
        // Form açıldıktan sonra scroll et
        setTimeout(() => {
            const orderFormElement = document.querySelector('.order-form');
            if (orderFormElement) {
                orderFormElement.scrollIntoView({ behavior: 'smooth', block: 'start' });
            } else {
                const orderDetailsElement = document.querySelector('.order-details');
                if (orderDetailsElement) {
                    orderDetailsElement.scrollIntoView({ behavior: 'smooth', block: 'start' });
                } else {
                    window.scrollTo({ top: 0, behavior: 'smooth' });
                }
            }
        }, 100);
    };
    // Sipariş kopyala
    const handleCopyOrder = (order) => {
        setSelectedProductType(order.productType);
        // Plise için geriye dönük uyumluluk dönüşümü
        let pliseItems = order.pliseItems || [];
        if (order.productType === 'plise' && (!pliseItems || pliseItems.length === 0) && order.width && order.height) {
            pliseItems = [{
                id: Date.now(),
                width: order.width,
                height: order.height,
                usedM2: order.usedM2 || '1.00',
                quantity: order.quantity || '1',
                multisistem: order.multisistem || false,
                rowTotal: order.totalPrice || '0.00'
            }];
        }
        // Kopyalanan siparişin verilerini set et
        const copiedOrder = {
            roomName: order.roomName,
            productName: order.productName,
            productVariant: order.productVariant,
            width: order.width,
            height: order.height,
            pileModel: order.pileModel,
            pileDensity: order.pileDensity,
            usedM2: order.usedM2,
            usedCm: order.usedCm,
            usedMeter: order.usedMeter,
            olcuTamamlama: order.olcuTamamlama,
            unitPrice: order.unitPrice,
            extraPrice: order.extraPrice,
            capsuleUnitPrice: order.capsuleUnitPrice,
            capsulePrice: order.capsulePrice,
            jumboSystem: order.jumboSystem,
            reductor: order.reductor,
            bottomSkirt: order.bottomSkirt,
            closedCase: order.closedCase,
            laser: order.laser,
            mounting: order.mounting,
            backgroundCurtain: order.backgroundCurtain,
            blackout: order.blackout,
            crepTul: order.crepTul,
            multisistem: order.multisistem,
            honeycomb: order.honeycomb,
            standartPlise: order.standartPlise,
            fabricPrice: order.fabricPrice,
            mechanismType: order.mechanismType,
            mechanismPrice: order.mechanismPrice,
            accessoryPrice: order.accessoryPrice,
            accessories: order.accessories || [],
            rustikAksesuarlar: cloneRustikExtras(order.rustikAksesuarlar),
            rustikRaySecenekleri: cloneRustikExtras(order.rustikRaySecenekleri),
            ayakModel: order.ayakModel || '',
            ayakCap: order.ayakCap || '',
            ayakUzunluk: order.ayakUzunluk || '',
            ayakRenkKodu: order.ayakRenkKodu || '',
            ayakSayisi: order.ayakSayisi || '',
            ekAyakSayisi: order.ekAyakSayisi || '',
            ayakUcreti: order.ayakUcreti || 0,
            ayakUcretiCost: order.ayakUcretiCost || '',
            ayakSayisiManual: order.ayakSayisiManual || (
                String(order.ayakSayisi || '') !== String(calculateRustikAyakSayisi(order.usedCm))
            ),
            quantity: order.quantity,
            discount: order.discount,
            totalPrice: order.totalPrice,
            discountedPrice: order.discountedPrice,
            notes: order.notes,
            motorType: order.motorType || '',
            motorPrice: order.motorPrice || 0,
            manualExtraCost: order.manualExtraCost || '',
            unitType: order.unitType || 'Adet',
            itemMeter: order.itemMeter || '',
            motorPriceCurrency: order.motorPriceCurrency || '',
            direction: order.direction || '',
            // Maliyet alanları — kopyalamada korunmalı
            unitCost: order.unitCost || '',
            baseUnitCost: order.baseUnitCost || '',
            totalCost: order.totalCost || '0.00',
            fabricCost: order.fabricCost || '',
            mountingCost: order.mountingCost || '',
            // Plise Özel
            pliseItems: pliseItems,
            pliseProduct1Name: order.pliseProduct1Name || (order.productType === 'plise' ? order.productName : ''),
            pliseProduct1Price: order.pliseProduct1Price || (order.productType === 'plise' ? (order.unitPrice || 0) : 0),
            pliseProduct2Name: order.pliseProduct2Name || '',
            pliseProduct2Price: order.pliseProduct2Price || 0,
            pliseProduct1Cost: order.pliseProduct1Cost || 0,
            pliseProduct2Cost: order.pliseProduct2Cost || 0,
            pliseProduct1Variation: order.pliseProduct1Variation || '',
            pliseProduct2Variation: order.pliseProduct2Variation || '',
            pliseCaseVariation: order.pliseCaseVariation || '',
            hasStorJumbo: order.hasStorJumbo || false,
            hasStorAltEtekDilim: order.hasStorAltEtekDilim || false,
            hasStorKapaliKasa: order.hasStorKapaliKasa || false,
            hasStorAltEtekLazer: order.hasStorAltEtekLazer || false,
            hasStorDekoratifLama: order.hasStorDekoratifLama || false,
            hasStorDekoratifKasa: order.hasStorDekoratifKasa || false,
            hasStorAltEtekBeyazIs: order.hasStorAltEtekBeyazIs || false,
            storJumboAmount: order.storJumboAmount || 0,
            storAltEtekDilimAmount: order.storAltEtekDilimAmount || 0,
            storKapaliKasaAmount: order.storKapaliKasaAmount || 0,
            storAltEtekLazerAmount: order.storAltEtekLazerAmount || 0,
            storDekoratifLamaAmount: order.storDekoratifLamaAmount || 0,
            storDekoratifKasaAmount: order.storDekoratifKasaAmount || 0,
            storAltEtekBeyazIsAmount: order.storAltEtekBeyazIsAmount || 0,
            hasZebraJumbo: order.hasZebraJumbo || false,
            hasZebraAltEtekDilim: order.hasZebraAltEtekDilim || false,
            hasZebraKapaliKasa: order.hasZebraKapaliKasa || false,
            zebraJumboAmount: order.zebraJumboAmount || 0,
            zebraAltEtekDilimAmount: order.zebraAltEtekDilimAmount || 0,
            zebraKapaliKasaAmount: order.zebraKapaliKasaAmount || 0,
            hasDikeyFon: order.hasDikeyFon || false,
            dikeyFonName: order.dikeyFonName || '',
            dikeyFonCount: order.dikeyFonCount || 1,
            dikeyFonAmount: order.dikeyFonAmount || 0,
            // Ahşap Jaluzi
            sistemTipi: order.sistemTipi || '',
            hasZigil: order.hasZigil || false,
            zigilAmount: order.zigilAmount || 0,
            hasReduktor: order.hasReduktor || false,
            hasKurdela: order.hasKurdela || false,
            baseUnitPrice: order.baseUnitPrice || '',
            // Metal Jaluzi
            hasMetalZigil: order.hasMetalZigil || false,
            metalZigilAmount: order.metalZigilAmount || 0,
            hasMetalReduktor: order.hasMetalReduktor || false,
            hasIkiCamArasi: order.hasIkiCamArasi || false,
            ikiCamArasiAmount: order.ikiCamArasiAmount || 0
        };
        // Plise için hesaplamaları önden yap (maliyet dahil)
        if (order.productType === 'plise') {
            const { items, totalPrice, discountedPrice, totalCost } = calculatePliseBatchTotals(
                pliseItems,
                copiedOrder.pliseProduct1Price,
                copiedOrder.pliseProduct2Price,
                copiedOrder.mounting,
                copiedOrder.discount,
                copiedOrder.quantity,
                copiedOrder.extraPrice,
                copiedOrder.pliseProduct1Cost,
                copiedOrder.pliseProduct2Cost,
                copiedOrder.mountingCost
            );
            copiedOrder.pliseItems = items;
            copiedOrder.totalPrice = totalPrice;
            copiedOrder.discountedPrice = discountedPrice;
            copiedOrder.totalCost = totalCost;
        }
        if (order.productType === 'rustik') {
            if (order.width) {
                copiedOrder.usedCm = copiedOrder.usedCm || calculateRustikUsedCm(order.width);
            }
            const { totalPrice, discountedPrice, totalCost } = calculateRustikTotals(copiedOrder);
            copiedOrder.totalPrice = totalPrice;
            copiedOrder.discountedPrice = discountedPrice;
            copiedOrder.totalCost = totalCost;
        }
        setNewOrder(copiedOrder);
        setIsAddingOrder(true); // Yeni sipariş moduna geç
        setEditingOrderId(null); // Edit modu değil, kopyalama modu
        if (order.productType === 'rustik') {
            setRustikFormSessionKey((k) => k + 1);
        }
        // Form açıldıktan sonra scroll et
        setTimeout(() => {
            const orderFormElement = document.querySelector('.order-form');
            if (orderFormElement) {
                orderFormElement.scrollIntoView({ behavior: 'smooth', block: 'start' });
            } else {
                const orderDetailsElement = document.querySelector('.order-details');
                if (orderDetailsElement) {
                    orderDetailsElement.scrollIntoView({ behavior: 'smooth', block: 'start' });
                } else {
                    window.scrollTo({ top: 0, behavior: 'smooth' });
                }
            }
        }, 100);
    };
    // Toplam tutarları hesapla (memoize - orders/vade/iskonto değişmedikçe yeniden hesaplanmaz)
    const totalAmount = useMemo(
        () => orders.reduce((sum, order) => sum + parseFloat(order.totalPrice || 0), 0),
        [orders]
    );
    // İndirimli tutarı hesapla
    const discountedTotalAmount = useMemo(
        () => orders.reduce((sum, order) => {
            if (order.discountedPrice && parseFloat(order.discount) > 0) {
                return sum + parseFloat(order.discountedPrice || 0);
            }
            return sum + parseFloat(order.totalPrice || 0);
        }, 0),
        [orders]
    );
    // Genel iskonto hesaplama
    const iskontoluToplam = useMemo(
        () => genelIskontoYuzde && parseFloat(genelIskontoYuzde) > 0
            ? totalAmount - (totalAmount * parseFloat(genelIskontoYuzde) / 100)
            : totalAmount,
        [totalAmount, genelIskontoYuzde]
    );
    // Vadeli toplam hesaplama (vade farkı tüm siparişe uygulanır)
    const vadeliToplam = useMemo(() => {
        const vYuzde = parseFloat(vadeliYuzde) || 0;
        if (!vYuzde) return totalAmount;
        return totalAmount + (totalAmount * vYuzde / 100);
    }, [totalAmount, vadeliYuzde]);
    const isDealerSession = parentCollectionName === 'dealer_sessions';
    const computeRemainingBalance = (total, paid) => {
        const raw = total - paid;
        return isDealerSession ? raw : Math.max(0, raw);
    };
    const isBalanceClosed = (remaining) => {
        if (isDealerSession) return remaining <= 0.009;
        return remaining === 0;
    };
    const toplamOdeme = useMemo(() => {
        return calculateCustomerGrandTotal(
            { vadeliYuzde, genelIskontoYuzde },
            orders
        );
    }, [orders, vadeliYuzde, genelIskontoYuzde]);
    // Efektif toplam: manuel override varsa onu, yoksa hesaplanan toplamOdeme'yi kullan
    const effectiveTotal = manualTotalOverride !== null ? manualTotalOverride : toplamOdeme;
    // Kalan ödeme hesaplama
    const remainingAmount = useMemo(() => {
        const paid = parseFloat(paidAmount) || 0;
        if (isPaymentCompleted) return 0;
        return computeRemainingBalance(effectiveTotal, paid);
    }, [effectiveTotal, paidAmount, isPaymentCompleted, isDealerSession]);
    // Ödeme bilgileri değiştiğinde parent'a bildir (yeni müşteri ekleme modu için)
    useEffect(() => {
        if (!isEditMode && onCustomerSaved && customerId && !customerId.startsWith('temp_')) {
            const currentRemaining = remainingAmount;
            const paidValue = parseFloat(paidAmount) || 0;
            onCustomerSaved({
                paidAmount: paidValue,
                isPaymentCompleted: isPaymentCompleted,
                remainingAmount: currentRemaining
            });
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [paidAmount, isPaymentCompleted, totalAmount, discountedTotalAmount, toplamOdeme, effectiveTotal, isEditMode, customerId, orders]);
    // Ödeme tamamlandı checkbox değiştiğinde
    const handlePaymentCompletedChange = async (checked) => {
        if (!customerId || customerId.startsWith('temp_')) {
            setIsPaymentCompleted(checked);
            return;
        }
        try {
            setIsSavingPayment(true);
            setIsPaymentCompleted(checked);
            // Ödeme geçmişindeki gerçek ödenen toplam
            const historyPaidTotal = paymentHistory.reduce((sum, p) => sum + parseFloat(p.amount || 0), 0);
            // Hedef ödenen ve kalan tutar
            let targetPaidAmount = historyPaidTotal;
            let targetRemaining = computeRemainingBalance(calculateTotalOrderAmount(), targetPaidAmount);
            if (checked) {
                // Tamamlandı işaretlenince ödenen = toplam, kalan = 0
                targetPaidAmount = calculateTotalOrderAmount();
                targetRemaining = 0;
            }
            await updateDoc(doc(db, parentCollectionName, customerId), {
                paidAmount: targetPaidAmount,
                remainingAmount: targetRemaining,
                isPaymentCompleted: checked,
                updatedAt: new Date()
            });
            // Local state güncelle
            setPaidAmount(targetPaidAmount.toString());
            // Parent'a bildir (edit modunda da güncel kalsın)
            if (onCustomerSaved) {
                onCustomerSaved({
                    ...customerInfo,
                    paidAmount: targetPaidAmount,
                    remainingAmount: targetRemaining,
                    isPaymentCompleted: checked
                });
            }
        } catch (error) {
            console.error('Ödeme tamamlandı güncellenirken hata:', error);
            showToast('Ödeme tamamlandı bilgisi kaydedilemedi: ' + error.message);
            // Hata durumunda checkbox durumunu geri al
            setIsPaymentCompleted(!checked);
        } finally {
            setIsSavingPayment(false);
        }
    };
    // Ödeme bilgilerini kaydet
    // Yeni ödeme ekleme fonksiyonu
    const handleAddPayment = async () => {
        if (!customerId || customerId.startsWith('temp_')) {
            return;
        }
        const paymentAmount = parseFloat(newPaymentAmount);
        if (!paymentAmount || paymentAmount <= 0) {
            showToast('Lütfen geçerli bir ödeme tutarı girin.');
            return;
        }
        try {
            setIsAddingPayment(true);
            // Yeni ödeme kaydı oluştur
            const newPayment = {
                id: Date.now().toString(),
                amount: paymentAmount,
                type: newPaymentType,
                date: new Date().toISOString(),
                createdBy: user?.uid || null
            };
            // Güncel ödeme geçmişi
            const updatedPaymentHistory = [...paymentHistory, newPayment];
            // Toplam ödenen tutarı hesapla
            const totalPaid = updatedPaymentHistory.reduce((sum, p) => sum + p.amount, 0);
            // Kalan ödemeyi hesapla
            const totalAmount = calculateTotalOrderAmount();
            const newRemaining = computeRemainingBalance(totalAmount, totalPaid);
            const paymentCompleted = isBalanceClosed(newRemaining);
            await updateDoc(doc(db, parentCollectionName, customerId), {
                paidAmount: totalPaid,
                paymentHistory: updatedPaymentHistory,
                isPaymentCompleted: paymentCompleted,
                remainingAmount: newRemaining,
                updatedAt: new Date()
            });
            // Local state'leri güncelle
            setPaymentHistory(updatedPaymentHistory);
            setPaidAmount(totalPaid.toString());
            setIsPaymentCompleted(paymentCompleted);
            setNewPaymentAmount('');
            setNewPaymentType(PAYMENT_TYPES.CASH);
            // Parent'a güncellenmiş bilgileri bildir
            const updatedCustomerInfo = {
                ...customerInfo,
                paidAmount: totalPaid,
                paymentHistory: updatedPaymentHistory,
                isPaymentCompleted: paymentCompleted,
                remainingAmount: newRemaining
            };
            if (onCustomerSaved) {
                onCustomerSaved(updatedCustomerInfo);
            }
        } catch (error) {
            console.error('Ödeme eklenirken hata:', error);
            showToast('Ödeme eklenirken bir hata oluştu: ' + error.message);
        } finally {
            setIsAddingPayment(false);
        }
    };
    // Ödeme iptal fonksiyonu
    const handleCancelPayment = async (paymentToCancel) => {
        if (!customerId || customerId.startsWith('temp_')) return;
        try {
            setIsAddingPayment(true);
            // Ödemeyi listeden çıkar
            const updatedPaymentHistory = paymentHistory.filter(p => p.id !== paymentToCancel.id);
            // Yeni toplam ödenen tutarı hesapla
            const totalPaid = updatedPaymentHistory.reduce((sum, p) => sum + parseFloat(p.amount || 0), 0);
            // Kalan ödemeyi hesapla
            const totalAmount = calculateTotalOrderAmount();
            const newRemaining = computeRemainingBalance(totalAmount, totalPaid);
            const paymentCompleted = isBalanceClosed(newRemaining);
            await updateDoc(doc(db, parentCollectionName, customerId), {
                paidAmount: totalPaid,
                paymentHistory: updatedPaymentHistory,
                isPaymentCompleted: paymentCompleted,
                remainingAmount: newRemaining,
                updatedAt: new Date()
            });
            // Local state'leri güncelle
            setPaymentHistory(updatedPaymentHistory);
            setPaidAmount(totalPaid.toString());
            setIsPaymentCompleted(paymentCompleted);
            setCancelPaymentConfirm(null);
            // Parent'a güncellenmiş bilgileri bildir
            if (onCustomerSaved) {
                onCustomerSaved({
                    ...customerInfo,
                    paidAmount: totalPaid,
                    paymentHistory: updatedPaymentHistory,
                    isPaymentCompleted: paymentCompleted,
                    remainingAmount: newRemaining
                });
            }
        } catch (error) {
            console.error('Ödeme iptal edilirken hata:', error);
            showToast('Ödeme iptal edilirken bir hata oluştu: ' + error.message);
        } finally {
            setIsAddingPayment(false);
        }
    };
    // Toplam sipariş tutarını hesapla (Manuel override varsa onu kullan)
    const calculateTotalOrderAmount = () => {
        return effectiveTotal;
    };
    // Manuel toplam düzenleme - kaydet
    const handleSaveManualTotal = async () => {
        const parsed = parseFloat(String(editingTotalValue).replace(',', '.'));
        if (isNaN(parsed) || parsed < 0) {
            showToast('Geçerli bir tutar girin.', 'error');
            return;
        }
        setIsSavingTotal(true);
        setManualTotalOverride(parsed);
        setIsEditingTotal(false);
        if (!customerId || customerId.startsWith('temp_')) {
            setIsSavingTotal(false);
            return;
        }
        try {
            const paid = parseFloat(paidAmount) || 0;
            const newRemaining = computeRemainingBalance(parsed, paid);
            await updateDoc(doc(db, parentCollectionName, customerId), {
                manualTotalOverride: parsed,
                totalOrderAmount: parsed,
                remainingAmount: newRemaining,
                updatedAt: new Date()
            });
            if (onCustomerSaved) {
                onCustomerSaved({
                    ...customerInfo,
                    id: customerId,
                    manualTotalOverride: parsed,
                    totalOrderAmount: parsed,
                    remainingAmount: newRemaining,
                    updateType: 'manualTotalSave'
                });
            }
            if (!onCustomerSaved) {
                showToast('Genel toplam güncellendi.', 'success');
            }
        } catch (error) {
            showToast('Güncelleme sırasında hata oluştu: ' + error.message, 'error');
        } finally {
            setIsSavingTotal(false);
        }
    };
    // Manuel toplam düzenleme - otomatik hesaplamaya dön
    const handleRevertManualTotal = async () => {
        setManualTotalOverride(null);
        setIsEditingTotal(false);
        if (!customerId || customerId.startsWith('temp_')) return;
        try {
            const paid = parseFloat(paidAmount) || 0;
            const newRemaining = computeRemainingBalance(toplamOdeme, paid);
            await updateDoc(doc(db, parentCollectionName, customerId), {
                manualTotalOverride: deleteField(),
                totalOrderAmount: toplamOdeme,
                remainingAmount: newRemaining,
                updatedAt: new Date()
            });
            if (onCustomerSaved) {
                onCustomerSaved({
                    ...customerInfo,
                    id: customerId,
                    manualTotalOverride: null,
                    totalOrderAmount: toplamOdeme,
                    remainingAmount: newRemaining,
                    updateType: 'manualTotalRevert'
                });
            }
            if (!onCustomerSaved) {
                showToast('Otomatik hesaplamaya döndürüldü.', 'success');
            }
        } catch (error) {
            showToast('Güncelleme sırasında hata oluştu: ' + error.message, 'error');
        }
    };
    const handleSavePayment = async () => {
        if (!customerId || customerId.startsWith('temp_')) {
            return;
        }
        try {
            setIsSavingPayment(true);
            // Ödenen tutarı parse et - boş string kontrolü
            const paidAmountValue = (paidAmount && paidAmount.trim() !== '') ? parseFloat(paidAmount) : 0;
            // Kalan ödemeyi tekrar hesapla (güncel değerlerle)
            const currentRemaining = remainingAmount;
            await updateDoc(doc(db, parentCollectionName, customerId), {
                paidAmount: paidAmountValue,
                isPaymentCompleted: isPaymentCompleted,
                remainingAmount: currentRemaining,
                paymentHistory: paymentHistory,
                updatedAt: new Date()
            });
            // Local state'i güncelle - müşteri bilgilerini güncelle
            const updatedCustomerInfo = {
                ...customerInfo,
                paidAmount: paidAmountValue,
                isPaymentCompleted: isPaymentCompleted,
                remainingAmount: currentRemaining,
                paymentHistory: paymentHistory
            };
            // Parent'a güncellenmiş bilgileri bildir (sadece ilgili müşteriyi güncelle, tüm listeyi yenileme)
            // onCustomerSaved callback'i zaten "Ödeme bilgisi başarıyla güncellendi!" mesajını gösteriyor
            if (onCustomerSaved) {
                onCustomerSaved(updatedCustomerInfo);
            }
            // onSaveVadeliSuccess sadece vadeli yüzde kaydedildiğinde çağrılmalı, ödeme bilgisi kaydedildiğinde değil
        } catch (error) {
            console.error('Ödeme bilgileri kaydedilirken hata:', error);
            showToast('Ödeme bilgileri kaydedilirken bir hata oluştu: ' + error.message);
        } finally {
            setIsSavingPayment(false);
        }
    };
    return (
        <div className="order-details">
            <div className="order-header">
                <div className="order-header-left">
                    <h4>Sipariş Detayları</h4>
                    {customerName && (
                        <div className="customer-info">
                            <div className="customer-name">
                                <strong>Müşteri:</strong> {customerName} {customerInfo?.tcNo && <span style={{ marginLeft: '10px', fontSize: '12px', color: '#64748b' }}>(TC/Vergi No: {customerInfo.tcNo})</span>}
                            </div>
                            <div className="order-status-selector">
                                <label>Sipariş Durumu:</label>
                                {isWholesale ? (
                                    <select
                                        value={orderStatus || 'new_order'}
                                        onChange={(e) => { if (onOrderStatusChange) onOrderStatusChange(e.target.value); }}
                                        disabled={isLoading}
                                    >
                                        <option value="new_order">Yeni Sipariş</option>
                                        <option value="preparing">Hazırlanıyor</option>
                                        <option value="shipped">Kargoya Verildi</option>
                                        <option value="delivered">Teslim Edildi</option>
                                        <option value="cancelled">İptal</option>
                                    </select>
                                ) : (
                                    <select
                                        value={orderStatus || ORDER_STATUS.NEW_ORDER}
                                        onChange={(e) => {
                                            if (onOrderStatusChange) {
                                                if (isMontajci && e.target.value !== ORDER_STATUS.DELIVERED) {
                                                    return;
                                                }
                                                onOrderStatusChange(e.target.value);
                                            }
                                        }}
                                        disabled={isLoading || (isMontajci && orderStatus === ORDER_STATUS.DELIVERED)}
                                    >
                                        {isMontajci ? (
                                            <>
                                                <option value={orderStatus}>{ORDER_STATUS_LABELS[orderStatus]}</option>
                                                {orderStatus !== ORDER_STATUS.DELIVERED && (
                                                    <option value={ORDER_STATUS.DELIVERED}>{ORDER_STATUS_LABELS[ORDER_STATUS.DELIVERED]}</option>
                                                )}
                                            </>
                                        ) : (
                                            Object.entries(ORDER_STATUS_LABELS).map(([key, label]) => (
                                                <option key={key} value={key}>{label}</option>
                                            ))
                                        )}
                                    </select>
                                )}
                            </div>
                        {/* Bayi modunda sipariş ve teslimat tarihleri */}
                        {isWholesale && (
                            <div style={{
                                display: 'flex',
                                gap: '20px',
                                marginTop: '12px',
                                flexWrap: 'wrap',
                                alignItems: 'center'
                            }}>
                                <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                                    <label style={{
                                        fontSize: '0.85rem',
                                        fontWeight: 600,
                                        color: '#475569',
                                        whiteSpace: 'nowrap'
                                    }}>
                                        <i className="fas fa-calendar-alt" style={{ marginRight: '5px', color: '#6366f1' }}></i>
                                        Sipariş Tarihi:
                                    </label>
                                    <input
                                        type="date"
                                        value={sessionOrderDate}
                                        onChange={(e) => handleSessionDateChange('orderDate', e.target.value)}
                                        disabled={isSavingSessionDates}
                                        style={{
                                            padding: '6px 10px',
                                            border: '1.5px solid #c7d2fe',
                                            borderRadius: '8px',
                                            fontSize: '0.88rem',
                                            color: '#1e293b',
                                            background: '#f5f3ff',
                                            cursor: 'pointer',
                                            outline: 'none'
                                        }}
                                    />
                                </div>
                                <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                                    <label style={{
                                        fontSize: '0.85rem',
                                        fontWeight: 600,
                                        color: '#475569',
                                        whiteSpace: 'nowrap'
                                    }}>
                                        <i className="fas fa-truck" style={{ marginRight: '5px', color: '#10b981' }}></i>
                                        Teslimat Tarihi:
                                    </label>
                                    <input
                                        type="date"
                                        value={sessionDeliveryDate}
                                        onChange={(e) => handleSessionDateChange('deliveryDate', e.target.value)}
                                        disabled={isSavingSessionDates}
                                        style={{
                                            padding: '6px 10px',
                                            border: '1.5px solid #a7f3d0',
                                            borderRadius: '8px',
                                            fontSize: '0.88rem',
                                            color: '#1e293b',
                                            background: '#ecfdf5',
                                            cursor: 'pointer',
                                            outline: 'none'
                                        }}
                                    />
                                </div>
                                {isSavingSessionDates && (
                                    <span style={{ fontSize: '0.8rem', color: '#94a3b8' }}>
                                        <i className="fas fa-spinner fa-spin" style={{ marginRight: '4px' }}></i>
                                        Kaydediliyor...
                                    </span>
                                )}
                            </div>
                        )}
                        </div>
                    )}
                </div>
                <div className="order-header-right">
                    {permissions?.canCreateOrder && (
                        <button
                            onClick={() => {
                                if (isAddingOrder) {
                                    setIsAddingOrder(false);
                                    setEditingOrderId(null);
                                    setSelectedProductType('');
                                    setNewOrder({
                                        roomName: '',
                                        productName: '',
                                        productVariant: '',
                                        width: '',
                                        height: '',
                                        pileModel: '',
                                        pileDensity: '',
                                        usedM2: '',
                                        unitPrice: '',
                                        extraPrice: '',
                                        capsuleUnitPrice: '',
                                        capsulePrice: '',
                                        jumboSystem: '',
                                        reductor: '',
                                        bottomSkirt: '',
                                        closedCase: '',
                                        laser: '',
                                        mounting: '',
                                        backgroundCurtain: '',
                                        pliseProduct1Name: '',
                                        pliseProduct1Price: '',
                                        pliseProduct1Variation: '',
                                        pliseProduct2Name: '',
                                        pliseProduct2Price: '',
                                        pliseProduct2Variation: '',
                                        fabricPrice: '',
                                        mechanismType: '',
                                        mechanismPrice: '',
                                        accessoryPrice: '',
                                        accessories: [],
                                        quantity: '',
                                        discount: '',
                                        totalPrice: '',
                                        discountedPrice: '',
                                        notes: ''
                                    });
                                } else {
                                    setIsAddingOrder(true);
                                    setEditingOrderId(null);
                                    setSelectedProductType('');
                                    setNewOrder({
                                        roomName: '',
                                        productName: '',
                                        productVariant: '',
                                        width: '',
                                        height: '',
                                        pileModel: '',
                                        pileDensity: '',
                                        usedM2: '',
                                        unitPrice: '',
                                        extraPrice: '',
                                        capsuleUnitPrice: '',
                                        capsulePrice: '',
                                        jumboSystem: '',
                                        reductor: '',
                                        bottomSkirt: '',
                                        closedCase: '',
                                        laser: '',
                                        mounting: '',
                                        backgroundCurtain: '',
                                        pliseProduct1Name: '',
                                        pliseProduct1Price: '',
                                        pliseProduct1Variation: '',
                                        pliseProduct2Name: '',
                                        pliseProduct2Price: '',
                                        pliseProduct2Variation: '',
                                        fabricPrice: '',
                                        mechanismType: '',
                                        mechanismPrice: '',
                                        accessoryPrice: '',
                                        accessories: [],
                                        quantity: '',
                                        discount: '',
                                        totalPrice: '',
                                        discountedPrice: '',
                                        notes: ''
                                    });
                                }
                            }}
                            className={`add-order-btn ${!isAddingOrder && orders.length === 0 ? 'pulse-button' : ''}`}
                            disabled={isLoading}
                        >
                            <i className={isAddingOrder ? "fas fa-times" : "fas fa-plus"}></i>
                            {isAddingOrder ? ' İptal' : ' Sipariş Ekle'}
                        </button>
                    )}
                </div>
            </div>
            {isAddingOrder && (
                <OrderForm
                    isAddingOrder={isAddingOrder}
                    editingOrderId={editingOrderId}
                    selectedProductType={selectedProductType}
                    newOrder={newOrder}
                    isLoading={isLoading}
                    customerInfo={customerInfo}
                    customerId={customerId}
                    onProductTypeChange={handleProductTypeChange}
                    onFieldChange={handleFieldChange}
                    onSubmit={handleAddOrder}
                    onCancel={() => setIsAddingOrder(false)}
                    pileModels={pileModels}
                    isWholesale={isWholesale}
                    rustikFormSessionKey={rustikFormSessionKey}
                />
            )}
            {/* Sipariş Listesi */}
            <div className="orders-list">
                {orders.length === 0 ? (
                    <div className="no-orders">
                        <i className="fas fa-shopping-basket fa-3x" style={{ color: '#cbd5e1', marginBottom: '15px' }}></i>
                        <p>Henüz sipariş eklenmemiş.</p>
                        {permissions?.canCreateOrder && (
                            <button
                                className="secondary-add-btn"
                                onClick={() => setIsAddingOrder(true)}
                            >
                                <i className="fas fa-plus"></i> İlk Siparişi Ekle
                            </button>
                        )}
                    </div>
                ) : (
                    <>
                        {/* Desktop Tablo Görünümü */}
                        <table className="orders-table">
                            <thead>
                                <tr>
                                    <th>Ürün Tipi</th>
                                    {!isWholesale && <th>Oda</th>}
                                    <th>Ürün Adı</th>
                                    <th>Ölçüler (cm/m)</th>
                                    <th>Pile Modeli</th>
                                    <th>Kullanılan Mt/Mt²</th>
                                    <th>Birim Fiyat</th>
                                    <th>Adet</th>
                                    <th>Toplam</th>
                                    <th>İndirimli</th>
                                    <th>Not</th>
                                    <th>İşlemler</th>
                                </tr>
                            </thead>
                            <tbody>
                                {orders.map((order) => (
                                    <tr key={order.id}>
                                        <td className="product-type-cell">
                                            <span>{PRODUCT_TYPES[order.productType]?.label || order.productType}</span>
                                        </td>
                                        {!isWholesale && <td>{order.roomName}</td>}
                                        <td>
                                            {order.productType === 'plise' ? (
                                                <>
                                                    {order.pliseProduct1Name}
                                                    {order.pliseProduct2Name && ` + ${order.pliseProduct2Name}`}
                                                </>
                                            ) : order.productType === 'rustik' ? (
                                                <>
                                                    <div className="product-name-text">{order.productName}{order.productVariant ? ` - ${order.productVariant}` : ''}</div>
                                                    <div className="product-sub-info" style={{ fontSize: '11px', color: '#64748b', marginTop: '2px' }}>
                                                        {order.ayakModel && <span>{order.ayakModel}</span>}
                                                        {order.ayakCap && <span> | Çap: {order.ayakCap}</span>}
                                                        {order.ayakUzunluk && <span> | Uz: {order.ayakUzunluk}</span>}
                                                        {order.ayakRenkKodu && <span> | Renk: {order.ayakRenkKodu}</span>}
                                                    </div>
                                                    {(order.rustikAksesuarlar?.length > 0 || order.rustikRaySecenekleri?.length > 0) && (
                                                        <div className="product-sub-info" style={{ fontSize: '10px', color: '#b04309', marginTop: '1px' }}>
                                                            <span>{(order.rustikAksesuarlar?.length || 0) + (order.rustikRaySecenekleri?.length || 0)} Ek Seçenek</span>
                                                        </div>
                                                    )}
                                                </>
                                            ) : (
                                                <>
                                                    {order.productName}{order.productVariant ? ` - ${order.productVariant}` : ''}
                                                    {order.direction ? ` (Yön: ${order.direction})` : ''}
                                                </>
                                            )}
                                        </td>
                                        <td>
                                            {order.width && `${order.width}`}
                                            {order.width && order.height && ' x '}
                                            {order.height && `${order.height}`}
                                        </td>
                                        <td>{order.pileModel || '-'}</td>
                                        <td>
                                            {order.productType === 'raySistemi'
                                                ? (order.usedMeter ? `${order.usedMeter} m` : '-')
                                                : order.productType === 'rustik'
                                                    ? (order.usedCm ? `${order.usedCm} cm` : '-')
                                                    : order.productType === 'plise'
                                                        ? (() => {
                                                            const items = order.pliseItems || [];
                                                            if (items.length === 0) return order.usedM2 || '-';
                                                            const total = items.reduce((sum, item) => {
                                                                const m2 = parseFloat(item.usedM2) || 0;
                                                                const qty = parseFloat(item.quantity) || 1;
                                                                return sum + (m2 * qty);
                                                            }, 0);
                                                            return total > 0 ? total.toFixed(2) : '-';
                                                        })()
                                                        : (order.usedM2 || '-')}
                                        </td>
                                        <td>
                                            {order.productType === 'plise' ? (
                                                <>
                                                    {formatPrice(order.pliseProduct1Price)}
                                                    {order.pliseProduct2Price && parseFloat(order.pliseProduct2Price) > 0 && ` + ${formatPrice(order.pliseProduct2Price)}`}
                                                    {' ₺'}
                                                </>
                                            ) : order.productType === 'katlamaliPerde' ? (
                                                `${formatPrice(order.fabricPrice)} / ${formatPrice(order.mechanismPrice)} ₺`
                                            ) : order.unitPrice && parseFloat(order.unitPrice) > 0 ? (
                                                `${formatPrice(order.unitPrice)} ₺`
                                            ) : (
                                                '-'
                                            )}
                                        </td>
                                        <td>
                                            {order.productType === 'plise'
                                                ? (order.pliseItems?.reduce((sum, item) => sum + (parseInt(item.quantity) || 0), 0) || 0)
                                                : (order.quantity || '1')}
                                        </td>
                                        <td>{formatPrice(order.totalPrice)} ₺</td>
                                        <td>
                                            {order.discountedPrice && parseFloat(order.discount) > 0 ? (
                                                <span className="discounted-price-display">
                                                    {formatPrice(order.discountedPrice)} ₺
                                                </span>
                                            ) : (
                                                '-'
                                            )}
                                        </td>
                                        <td>{order.notes}</td>
                                        <td>
                                            <div className="action-buttons">
                                                {/* Düzenleme - Montajcı hariç */}
                                                {permissions?.canEditOrder && (
                                                    <button
                                                        onClick={() => handleEditOrder(order)}
                                                        className="edit-btn"
                                                        disabled={isLoading}
                                                        title="Düzenle"
                                                    >
                                                        <i className="fas fa-edit"></i>
                                                    </button>
                                                )}
                                                {/* Kopyalama - Montajcı hariç */}
                                                {permissions?.canCreateOrder && (
                                                    <button
                                                        onClick={() => handleCopyOrder(order)}
                                                        className="copy-btn"
                                                        disabled={isLoading}
                                                        title="Kopyala"
                                                    >
                                                        <i className="fas fa-copy"></i>
                                                    </button>
                                                )}
                                                {/* Silme - Montajcı hariç */}
                                                {permissions?.canDeleteOrder && (
                                                    <button
                                                        onClick={() => handleDeleteConfirm(order)}
                                                        className="delete-btn"
                                                        disabled={isLoading}
                                                        title="Sil"
                                                    >
                                                        <i className="fas fa-trash"></i>
                                                    </button>
                                                )}
                                            </div>
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                        {/* Mobil ve Tablet Kart Görünümü */}
                        <div className="orders-cards">
                            {orders.map((order) => (
                                <div key={order.id} className="order-card">
                                    <div className="order-card-header">
                                        <div className="order-card-title">
                                            {PRODUCT_TYPES[order.productType]?.label || order.productType}
                                        </div>
                                        <div className="order-card-actions">
                                            {/* Düzenleme - Montajcı hariç */}
                                            {permissions?.canEditOrder && (
                                                <button
                                                    onClick={() => handleEditOrder(order)}
                                                    className="edit-btn"
                                                    disabled={isLoading}
                                                    title="Düzenle"
                                                >
                                                    <i className="fas fa-edit"></i>
                                                </button>
                                            )}
                                            {/* Kopyalama - Montajcı hariç */}
                                            {permissions?.canCreateOrder && (
                                                <button
                                                    onClick={() => handleCopyOrder(order)}
                                                    className="copy-btn"
                                                    disabled={isLoading}
                                                    title="Kopyala"
                                                >
                                                    <i className="fas fa-copy"></i>
                                                </button>
                                            )}
                                            {/* Silme - Montajcı hariç */}
                                            {permissions?.canDeleteOrder && (
                                                <button
                                                    onClick={() => handleDeleteConfirm(order)}
                                                    className="delete-btn"
                                                    disabled={isLoading}
                                                    title="Sil"
                                                >
                                                    <i className="fas fa-trash"></i>
                                                </button>
                                            )}
                                        </div>
                                    </div>
                                    <div className="order-card-content">
                                        <div className="order-card-item">
                                            <div className="order-card-label">Oda</div>
                                            <div className="order-card-value">{order.roomName}</div>
                                        </div>
                                        <div className="order-card-item">
                                            <div className="order-card-label">Ürün Adı</div>
                                            <div className="order-card-value">
                                                {order.productType === 'plise' ? (
                                                    <>
                                                        {order.pliseProduct1Name}
                                                        {order.pliseProduct2Name && ` + ${order.pliseProduct2Name}`}
                                                    </>
                                                ) : (
                                                    <>
                                                        {order.productName}{order.productVariant ? ` - ${order.productVariant}` : ''}
                                                        {order.direction ? ` (Yön: ${order.direction})` : ''}
                                                    </>
                                                )}
                                            </div>
                                        </div>
                                        {order.hasDikeyFon && (
                                            <div className="order-card-item">
                                                <div className="order-card-label">Fon</div>
                                                <div className="order-card-value">{order.dikeyFonName || 'Var'} ({order.dikeyFonCount || 1} Adet)</div>
                                            </div>
                                        )}
                                        <div className="order-card-item">
                                            <div className="order-card-label">Ölçüler (cm/m)</div>
                                            <div className="order-card-value">
                                                {order.width && `${order.width}`}
                                                {order.width && order.height && ' x '}
                                                {order.height && `${order.height}`}
                                            </div>
                                        </div>
                                        <div className="order-card-item">
                                            <div className="order-card-label">Pile Modeli</div>
                                            <div className="order-card-value">{order.pileModel || '-'}</div>
                                        </div>
                                        <div className="order-card-item">
                                            <div className="order-card-label">
                                                {order.productType === 'raySistemi' ? 'Kullanılan Metre' : order.productType === 'rustik' ? 'Kullanılan Cm' : 'Kullanılan M/Mt²'}
                                            </div>
                                            <div className="order-card-value">
                                                {order.productType === 'raySistemi' ? (
                                                    <>
                                                        {order.usedMeter ? `${order.usedMeter} m` : '-'}
                                                        {order.olcuTamamlama && <small style={{ display: 'block', color: '#64748b', fontSize: '11px' }}>({order.olcuTamamlama === '25cm' ? '25cm Tamamlama' : order.olcuTamamlama === '50cm' ? '50cm Tamamlama' : 'Ürün Boyutu'})</small>}
                                                    </>
                                                ) : order.productType === 'rustik' ? (
                                                    <>
                                                        {order.usedCm ? `${order.usedCm} cm` : '-'}
                                                        {(order.ayakSayisi || order.ekAyakSayisi) && (
                                                            <small style={{ display: 'block', color: '#64748b', fontSize: '11px' }}>
                                                                (Ayak: {order.ayakSayisi || 0}{order.ekAyakSayisi ? ` + Ek: ${order.ekAyakSayisi}` : ''})
                                                            </small>
                                                        )}
                                                    </>
                                                ) : order.productType === 'plise' ? (
                                                    (() => {
                                                        const items = order.pliseItems || [];
                                                        if (items.length === 0) return order.usedM2 || '-';
                                                        const total = items.reduce((sum, item) => {
                                                            const m2 = parseFloat(item.usedM2) || 0;
                                                            const qty = parseFloat(item.quantity) || 1;
                                                            return sum + (m2 * qty);
                                                        }, 0);
                                                        return total > 0 ? total.toFixed(2) : '-';
                                                    })()
                                                ) : (
                                                    order.usedM2 || '-'
                                                )}
                                            </div>
                                        </div>
                                        <div className="order-card-item">
                                            <div className="order-card-label">
                                                {order.productType === 'plise' ? 'Ürün Fiyatları' : order.productType === 'katlamaliPerde' ? 'Kumaş / Mek.' : 'Birim Fiyat'}
                                            </div>
                                            <div className="order-card-value">
                                                {order.productType === 'plise' ? (
                                                    <>
                                                        {formatPrice(order.pliseProduct1Price)}
                                                        {order.pliseProduct2Price && parseFloat(order.pliseProduct2Price) > 0 && ` + ${formatPrice(order.pliseProduct2Price)}`}
                                                        {' ₺'}
                                                    </>
                                                ) : order.productType === 'katlamaliPerde' ? (
                                                    `${formatPrice(order.fabricPrice)} / ${formatPrice(order.mechanismPrice)} ₺`
                                                ) : order.unitPrice && parseFloat(order.unitPrice) > 0 ? (
                                                    `${formatPrice(order.unitPrice)} ₺`
                                                ) : (
                                                    '-'
                                                )}
                                            </div>
                                        </div>
                                        <div className="order-card-item">
                                            <div className="order-card-label">Adet</div>
                                            <div className="order-card-value">
                                                {order.productType === 'plise'
                                                    ? (order.pliseItems?.reduce((sum, item) => sum + (parseInt(item.quantity) || 0), 0) || 0)
                                                    : (order.quantity || '1')}
                                            </div>
                                        </div>
                                        <div className="order-card-item">
                                            <div className="order-card-label">İndirimli</div>
                                            <div className="order-card-value">
                                                {order.discountedPrice && parseFloat(order.discount) > 0 ? (
                                                    <span className="discounted-price-display">
                                                        {formatPrice(order.discountedPrice)} ₺
                                                    </span>
                                                ) : (
                                                    '-'
                                                )}
                                            </div>
                                        </div>
                                        <div className="order-card-item">
                                            <div className="order-card-label">Toplam</div>
                                            <div className="order-card-value">{formatPrice(order.totalPrice)} ₺</div>
                                        </div>
                                        {order.notes && (
                                            <div className="order-card-item">
                                                <div className="order-card-label">Not</div>
                                                <div className="order-card-value">{order.notes}</div>
                                            </div>
                                        )}
                                    </div>
                                </div>
                            ))}
                        </div>
                    </>
                )}
                {orders.length > 0 && (
                    <div className="total-section">
                        <div className="totals-grid">
                            <div className="total-item">
                                <span className="total-label">Ara Toplam</span>
                                <span className="total-value">{formatPrice(totalAmount)} ₺</span>
                            </div>
                            {discountedTotalAmount !== totalAmount && (
                                <div className="total-item discounted">
                                    <span className="total-label">Ürün İndirimli Toplam</span>
                                    <span className="total-value">{formatPrice(discountedTotalAmount)} ₺</span>
                                </div>
                            )}
                            {/* Genel İskonto - Montajcı hariç */}
                            {!isMontajci && (
                                <div className="total-item iskontolu-input">
                                    <span className="total-label">Genel İskonto (%)</span>
                                    <input
                                        type="number"
                                        step="0.01"
                                        min="0"
                                        max="100"
                                        value={genelIskontoYuzde || ''}
                                        onChange={(e) => handleGenelIskontoYuzdeChange(e.target.value)}
                                        placeholder="%"
                                        className="vadeli-input-field"
                                    />
                                </div>
                            )}
                            {genelIskontoYuzde && parseFloat(genelIskontoYuzde) > 0 && (
                                <div className="total-item iskontolu">
                                    <span className="total-label">İskontolu Toplam</span>
                                    <span className="total-value">{formatPrice(iskontoluToplam)} ₺</span>
                                </div>
                            )}
                            {/* Vadeli - Montajcı hariç */}
                            {!isMontajci && (
                                <div className="total-item vadeli-input">
                                    <span className="total-label">Vade Farkı (%)</span>
                                    <input
                                        type="number"
                                        step="0.01"
                                        min="0"
                                        max="100"
                                        value={vadeliYuzde || ''}
                                        onChange={(e) => handleVadeliYuzdeChange(e.target.value)}
                                        placeholder="%"
                                        className="vadeli-input-field"
                                    />
                                </div>
                            )}
                            {vadeliYuzde && parseFloat(vadeliYuzde) > 0 && (
                                <div className="total-item vadeli">
                                    <span className="total-label">Vadeli Toplam</span>
                                    <span className="total-value">{formatPrice(vadeliToplam)} ₺</span>
                                </div>
                            )}
                            <div className="total-item toplam-odeme" style={{ position: 'relative' }}>
                                {/* Label satırı: metin + MANUEL rozeti yan yana */}
                                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                                    <span className="total-label" style={{ margin: 0 }}>Genel Toplam (Net)</span>
                                    {manualTotalOverride !== null && (
                                        <span style={{
                                            background: '#fef3c7', color: '#b45309',
                                            fontSize: '9px', fontWeight: '700', padding: '1px 6px',
                                            borderRadius: '20px', border: '1px solid #fcd34d',
                                            letterSpacing: '0.4px', flexShrink: 0
                                        }}>MANUEL</span>
                                    )}
                                </div>
                                {/* Değer satırı: tutar + tek ikon */}
                                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                                    <span className="total-value" style={{ flex: 1, minWidth: 0 }}>{formatPrice(effectiveTotal)} ₺</span>
                                    {!isMontajci && (
                                        <button
                                            onClick={() => {
                                                if (isEditingTotal) { setIsEditingTotal(false); return; }
                                                if (manualTotalOverride !== null) { handleRevertManualTotal(); return; }
                                                setEditingTotalValue(effectiveTotal.toFixed(2));
                                                setIsEditingTotal(true);
                                            }}
                                            title={
                                                isEditingTotal ? 'İptal'
                                                : manualTotalOverride !== null ? `Otomatik hesaplamaya dön (${formatPrice(toplamOdeme)} ₺)`
                                                : 'Düzenle'
                                            }
                                            style={{
                                                width: '24px', height: '24px', flexShrink: 0, marginLeft: '6px',
                                                border: manualTotalOverride !== null && !isEditingTotal ? '1px solid #fcd34d' : '1.5px solid #cbd5e1',
                                                borderRadius: '6px',
                                                background: manualTotalOverride !== null && !isEditingTotal ? '#fef3c7'
                                                    : isEditingTotal ? '#e2e8f0' : '#f8fafc',
                                                color: manualTotalOverride !== null && !isEditingTotal ? '#b45309' : '#475569',
                                                cursor: 'pointer', display: 'flex', alignItems: 'center',
                                                justifyContent: 'center', fontSize: '10px'
                                            }}>
                                            <i className={
                                                isEditingTotal ? 'fas fa-times'
                                                : manualTotalOverride !== null ? 'fas fa-undo-alt'
                                                : 'fas fa-pen'
                                            }></i>
                                        </button>
                                    )}
                                </div>

                                {/* Floating edit popover — diğer satırları itmez */}
                                {!isMontajci && isEditingTotal && (
                                    <div style={{
                                        position: 'absolute', top: 'calc(100% + 6px)', right: 0,
                                        zIndex: 200, width: '220px',
                                        background: '#fff',
                                        border: '1.5px solid #7dd3fc', borderRadius: '10px',
                                        boxShadow: '0 8px 24px rgba(0,0,0,0.13)',
                                        padding: '12px', display: 'flex', flexDirection: 'column', gap: '8px'
                                    }}>
                                        <div style={{ position: 'relative' }}>
                                            <input
                                                type="number"
                                                step="0.01"
                                                min="0"
                                                value={editingTotalValue}
                                                onChange={(e) => setEditingTotalValue(e.target.value)}
                                                onKeyDown={(e) => {
                                                    if (e.key === 'Enter') handleSaveManualTotal();
                                                    if (e.key === 'Escape') setIsEditingTotal(false);
                                                }}
                                                autoFocus
                                                style={{
                                                    width: '100%', padding: '8px 32px 8px 10px',
                                                    fontSize: '15px', fontWeight: '700',
                                                    border: '2px solid #38bdf8', borderRadius: '8px',
                                                    textAlign: 'right', outline: 'none',
                                                    background: '#f0f9ff', boxSizing: 'border-box', color: '#0f172a'
                                                }}
                                            />
                                            <span style={{
                                                position: 'absolute', right: '9px', top: '50%',
                                                transform: 'translateY(-50%)', fontSize: '13px',
                                                color: '#64748b', fontWeight: '600', pointerEvents: 'none'
                                            }}>₺</span>
                                        </div>
                                        <div style={{ display: 'flex', gap: '6px' }}>
                                            <button
                                                onClick={handleSaveManualTotal}
                                                disabled={isSavingTotal}
                                                style={{
                                                    flex: 1, padding: '7px 0', border: 'none',
                                                    borderRadius: '7px', background: '#16a34a',
                                                    color: '#fff', fontWeight: '600', fontSize: '12px',
                                                    cursor: isSavingTotal ? 'wait' : 'pointer',
                                                    display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '5px',
                                                    opacity: isSavingTotal ? 0.7 : 1
                                                }}>
                                                <i className={isSavingTotal ? 'fas fa-spinner fa-spin' : 'fas fa-check'}></i>
                                                Kaydet
                                            </button>
                                            <button
                                                onClick={() => setIsEditingTotal(false)}
                                                style={{
                                                    flex: 1, padding: '7px 0', border: '1.5px solid #e2e8f0',
                                                    borderRadius: '7px', background: '#f8fafc',
                                                    color: '#475569', fontWeight: '600', fontSize: '12px',
                                                    cursor: 'pointer', display: 'flex', alignItems: 'center',
                                                    justifyContent: 'center', gap: '5px'
                                                }}>
                                                <i className="fas fa-times"></i>
                                                İptal
                                            </button>
                                        </div>
                                    </div>
                                )}
                            </div>
                            {/* Ödeme Bilgileri - Montajcı hariç */}
                            {!isMontajci && (
                                <div className="total-item payment-section">
                                    <div className="payment-checkbox-wrapper">
                                        <label className="payment-checkbox-label">
                                            <input
                                                type="checkbox"
                                                checked={isPaymentCompleted}
                                                onChange={(e) => handlePaymentCompletedChange(e.target.checked)}
                                                disabled={isLoading || isSavingPayment}
                                                className="payment-checkbox"
                                            />
                                            <span>Ödeme Tamamlandı</span>
                                        </label>
                                    </div>
                                </div>
                            )}
                            {/* Toplam Ödenen */}
                            {!isMontajci && (
                                <div className="total-item paid-amount">
                                    <span className="total-label">Toplam Ödenen (₺)</span>
                                    <span className="total-value">
                                        {formatPrice(parseFloat(paidAmount) || 0)}
                                    </span>
                                </div>
                            )}
                            {/* Kalan Ödeme */}
                            {!isMontajci && (
                                <div className="total-item remaining-amount">
                                    <span className="total-label">
                                        {isDealerSession && remainingAmount < -0.009
                                            ? 'Alacaklı (Fazla Ödeme) (₺)'
                                            : 'Kalan Ödeme (₺)'}
                                    </span>
                                    <span className={`total-value ${
                                        isDealerSession && remainingAmount < -0.009
                                            ? 'payment-credit'
                                            : remainingAmount <= 0.009 ? 'payment-completed' : ''
                                    }`}>
                                        {(() => {
                                            if (isDealerSession) {
                                                const balanceInfo = formatDealerBalanceLabel(remainingAmount);
                                                if (balanceInfo.type === 'credit') {
                                                    return (
                                                        <>
                                                            <i className="fas fa-hand-holding-usd"></i>
                                                            {formatPrice(balanceInfo.amount)}
                                                        </>
                                                    );
                                                }
                                                if (balanceInfo.type === 'balanced') {
                                                    return (
                                                        <>
                                                            <i className="fas fa-check-circle"></i>
                                                            {isPaymentCompleted ? 'Ödeme Alındı' : '0.00'}
                                                        </>
                                                    );
                                                }
                                                return formatPrice(balanceInfo.amount);
                                            }
                                            if (remainingAmount === 0) {
                                                return (
                                                    <>
                                                        <i className="fas fa-check-circle"></i>
                                                        {isPaymentCompleted ? 'Ödeme Alındı' : '0.00'}
                                                    </>
                                                );
                                            }
                                            return formatPrice(remainingAmount);
                                        })()}
                                    </span>
                                </div>
                            )}
                        </div>
                        {/* Ödeme Ekleme Bölümü - Sadece düzenleme modunda, sipariş varsa ve Montajcı değilse görünür */}
                        {isEditMode && customerId && !customerId.startsWith('temp_') && orders.length > 0 && !isMontajci && !isPaymentCompleted && (
                            <div className="payment-add-section">
                                <h5><i className="fas fa-plus-circle"></i> Yeni Ödeme Ekle</h5>
                                <div className="payment-add-form">
                                    <div className="payment-add-row">
                                        <div className="payment-type-select">
                                            <label>Ödeme Tipi:</label>
                                            <select
                                                value={newPaymentType}
                                                onChange={(e) => setNewPaymentType(e.target.value)}
                                                disabled={isAddingPayment}
                                            >
                                                {Object.entries(PAYMENT_TYPE_LABELS).map(([key, label]) => (
                                                    <option key={key} value={key}>{label}</option>
                                                ))}
                                            </select>
                                        </div>
                                        <div className="payment-amount-input">
                                            <label>Tutar (₺):</label>
                                            <input
                                                type="number"
                                                step="0.01"
                                                min="0"
                                                value={newPaymentAmount}
                                                onChange={(e) => setNewPaymentAmount(e.target.value)}
                                                placeholder="0.00"
                                                disabled={isAddingPayment}
                                            />
                                        </div>
                                        <button
                                            type="button"
                                            onClick={handleAddPayment}
                                            className="payment-add-btn"
                                            disabled={isAddingPayment || isLoading || !newPaymentAmount}
                                        >
                                            {isAddingPayment ? (
                                                <i className="fas fa-spinner fa-spin"></i>
                                            ) : (
                                                <><i className="fas fa-plus"></i> Ekle</>
                                            )}
                                        </button>
                                    </div>
                                </div>
                            </div>
                        )}
                        {/* Ödeme Geçmişi Listesi */}
                        {!isMontajci && paymentHistory.length > 0 && (
                            <div className="payment-history-section">
                                <h5><i className="fas fa-history"></i> Ödeme Geçmişi</h5>
                                <div className="payment-history-list">
                                    {paymentHistory.map((payment, index) => (
                                        <div key={payment.id || index} className="payment-history-item">
                                            <div className="payment-history-info">
                                                <span className={`payment-type-badge payment-type-${payment.type}`}>
                                                    {PAYMENT_TYPE_LABELS[payment.type] || payment.type}
                                                </span>
                                                <span className="payment-amount">{formatPrice(payment.amount)} ₺</span>
                                            </div>
                                            <div className="payment-history-right">
                                                <div className="payment-history-date">
                                                    {new Date(payment.date).toLocaleDateString('tr-TR', {
                                                        day: '2-digit',
                                                        month: '2-digit',
                                                        year: 'numeric',
                                                        hour: '2-digit',
                                                        minute: '2-digit'
                                                    })}
                                                </div>
                                                <button
                                                    type="button"
                                                    className="payment-cancel-btn"
                                                    onClick={() => setCancelPaymentConfirm(payment)}
                                                    title="Ödemeyi İptal Et"
                                                >
                                                    <i className="fas fa-trash-alt"></i>
                                                    İptal
                                                </button>
                                            </div>
                                        </div>
                                    ))}
                                </div>
                            </div>
                        )}
                    </div>
                )}
            </div>
            {/* Fiyat Bilgilerini Kaydet Butonu - Sadece düzenleme modunda ve Montajcı değilse görünür */}
            {isEditMode && !isMontajci && (
                <div className="vadeli-save-section">
                    <button
                        type="button"
                        onClick={handleSavePricingFields}
                        className="vadeli-save-btn"
                        disabled={isSavingVadeli || isLoading}
                    >
                        {isSavingVadeli ? (
                            <>
                                <i className="fas fa-spinner fa-spin"></i>
                                <span>Kaydediliyor...</span>
                            </>
                        ) : (
                            <>
                                <i className="fas fa-save"></i>
                                <span>Fiyat Bilgilerini Kaydet</span>
                            </>
                        )}
                    </button>
                </div>
            )}
            {/* PDF Butonları */}
            <div className="pdf-buttons-section">
                <div className="pdf-buttons">
                    <button
                        onClick={() => generateCustomerPreOrderPDF(customerInfo, orders, totalAmount, discountedTotalAmount, genelIskontoYuzde, vadeliYuzde)}
                        className="pdf-btn customer-pdf"
                        title="Müşteri (Sipariş öncesi) PDF oluştur"
                    >
                        <i className="fas fa-file-pdf"></i>
                        <span>Müşteri (Sipariş öncesi)</span>
                    </button>
                    <button
                        onClick={() => generateCustomerPDF(customerInfo, orders, totalAmount, discountedTotalAmount, genelIskontoYuzde, vadeliYuzde, parseFloat(paidAmount) || 0, remainingAmount)}
                        className="pdf-btn customer-pdf"
                        title="Müşteri için PDF oluştur"
                    >
                        <i className="fas fa-file-pdf"></i>
                        <span>Müşteri</span>
                    </button>
                    <button
                        onClick={() => generateWorkshopPDF(customerInfo, orders)}
                        className="pdf-btn workshop-pdf"
                        title="Atölye için PDF oluştur"
                    >
                        <i className="fas fa-file-pdf"></i>
                        <span>Atölye</span>
                    </button>
                    <button
                        onClick={() => generateInstallationPDF(customerInfo, orders)}
                        className="pdf-btn installation-pdf"
                        title="Montaj için PDF oluştur"
                    >
                        <i className="fas fa-file-pdf"></i>
                        <span>Montaj</span>
                    </button>
                </div>
            </div>

            {/* Sipariş Silme Onay Modal */}
            {deleteConfirm && (
                <div className="modal-overlay">
                    <div className="modal-content">
                        <h3>Sipariş Sil</h3>
                        <p>
                            <strong>{deleteConfirm.productName}</strong> adlı siparişi silmek istediğinizden emin misiniz?
                        </p>
                        <p className="warning-text">Bu işlem geri alınamaz!</p>
                        <div className="modal-actions">
                            <button
                                onClick={handleDeleteOrder}
                                className="delete-confirm-btn"
                                disabled={isLoading}
                            >
                                {isLoading ? 'Siliniyor...' : 'Evet, Sil'}
                            </button>
                            <button
                                onClick={() => setDeleteConfirm(null)}
                                className="cancel-btn"
                                disabled={isLoading}
                            >
                                İptal
                            </button>
                        </div>
                    </div>
                </div>
            )}

            {/* Ödeme İptal Onay Modal */}
            {cancelPaymentConfirm && (
                <div className="modal-overlay">
                    <div className="modal-content">
                        <h3>Ödemeyi İptal Et</h3>
                        <p>
                            <strong>{formatPrice(cancelPaymentConfirm.amount)} ₺</strong> tutarındaki{' '}
                            <strong>{PAYMENT_TYPE_LABELS[cancelPaymentConfirm.type] || cancelPaymentConfirm.type}</strong>{' '}
                            ödemesini iptal etmek istediğinizden emin misiniz?
                        </p>
                        <p className="warning-text">Bu işlem, ödeme geçmişinden kaydı silecek ve tutarı kalan borca ekleyecektir.</p>
                        <div className="modal-actions">
                            <button
                                onClick={() => handleCancelPayment(cancelPaymentConfirm)}
                                className="delete-confirm-btn"
                                disabled={isAddingPayment}
                            >
                                {isAddingPayment ? 'İptal Ediliyor...' : 'Evet, İptal Et'}
                            </button>
                            <button
                                onClick={() => setCancelPaymentConfirm(null)}
                                className="cancel-btn"
                                disabled={isAddingPayment}
                            >
                                Vazgeç
                            </button>
                        </div>
                    </div>
                </div>
            )}

            {/* Sistem Seçim Onay Modal */}
            {showSystemConfirm && (
                <div className="modal-overlay">
                    <div className="modal-content">
                        <div style={{ textAlign: 'center', marginBottom: '20px' }}>
                            <i className="fas fa-exclamation-triangle" style={{ fontSize: '48px', color: '#f59e0b', marginBottom: '15px' }}></i>
                            <h3 style={{ marginTop: '15px', color: '#1f2937' }}>Sistem Seçilmedi</h3>
                            <p style={{ color: '#4b5563', marginBottom: '10px' }}>Herhangi bir sistem (Jumbo, Alt Etek, vb.) seçmediniz.</p>
                            <p style={{ color: '#4b5563', fontWeight: '500' }}>Siparişinizi bu şekilde kaydetmeyi onaylıyor musunuz?</p>
                        </div>
                        <div className="modal-actions">
                            <button
                                onClick={() => {
                                    setShowSystemConfirm(false);
                                    handleAddOrder(null, true);
                                }}
                                className="delete-confirm-btn"
                                style={{ backgroundColor: '#2563eb' }}
                                disabled={isLoading}
                            >
                                {isLoading ? 'Kaydediliyor...' : 'Evet, Onayla'}
                            </button>
                            <button
                                onClick={() => setShowSystemConfirm(false)}
                                className="cancel-btn"
                                disabled={isLoading}
                            >
                                Vazgeç
                            </button>
                        </div>
                    </div>
                </div>
            )}

            {/* Toast Notification (Portal ile Body'ye Eklenir) */}
            {toast.show && typeof document !== 'undefined' && createPortal(
                <div className={`toast-notification ${toast.type}`}>
                    <div className="toast-content">
                        <div className="toast-icon">
                            <i className={`fas ${toast.type === 'success' ? 'fa-check' : toast.type === 'error' ? 'fa-exclamation' : 'fa-info'}`}></i>
                        </div>
                        <div className="toast-message">{toast.message}</div>
                        <button className="toast-close" onClick={() => setToast(prev => ({ ...prev, show: false }))}>
                            <i className="fas fa-times"></i>
                        </button>
                    </div>
                    <div className="toast-progress"></div>
                </div>,
                document.body
            )}
        </div>
    );
};

export default OrderDetails;